import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import './UnityGame.css';
import {
  FaExpand,
  FaCompress,
  FaChevronRight,
  FaChevronLeft,
  FaVideo,
  FaEyeSlash,
  FaEye,
  FaSpinner,
  FaDesktop,
} from 'react-icons/fa';
import API from '../../API';
import logger from '../../utils/logger';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useWebSession } from '../../hooks/useWebSession';
import IVSRealtimePanel from './IVSRealtimePanel';
import IVSRealtimeChat, {
  type ClapSignal,
  type HandRaiseSignal,
  type IVSRealtimeChatRef,
} from './IVSRealtimeChat';
import * as IVSBroadcastClient from 'amazon-ivs-web-broadcast';
import RoomSelector, { RoomSelectorRef } from './RoomSelector';
import ModTools from './ModTools';
import Agenda from './Agenda';
import SocialInteraction from './SocialInteraction';
import { BreakoutClapDock, BreakoutClapOverlay } from './BreakoutClap';
import UnityGameDockApp from './UnityGameDockApp';
import HelpWidget from './HelpWidget';
import RoomUsersWidget from './RoomUsersWidget';
import PlayerClickedPeopleInfoPopover from './PlayerClickedPeopleInfoPopover';
import StagePlayer from './StagePlayer';
import NPCDialog from './NPCDialog';
import { BreakoutGuidelinesModal } from './BreakoutGuidelinesModal';
import LeavePodium from './LeavePodium';
import LeaveBreakoutRoom from './LeaveBreakoutRoom';
import OnboardingTour, { hasCompletedOnboarding, resetOnboarding } from './OnboardingTour';
import { FocusProvider, useFocus, useOverlay } from '../../contexts/FocusContext';
import type { GraphicsQuality } from './types';
import type { LocalVideoControlsApi } from './localVideoControlsApi';
import type { ScreenShareControlsApi, ScreenShareQuality } from './screenShareControlsApi';
import { SCREEN_SHARE_QUALITY_PRESETS } from './screenShareControlsApi';
import { DockLocalVideoPreview } from './dockLocalVideoPreview';
import { normalizeFacePinUserId } from './ivsFaceVideo';

// Import loading images
import intersectLoadingImage01 from '../images/intersect-test-loading-01.png';
import intersectLoadingImage02 from '../images/intersect-test-loading-02.png';
import intersectLoadingImage03 from '../images/intersect-test-loading-03.png';
import intersectLoadingImage04 from '../images/intersect-test-loading-04.png';
import intersectLoadingImage05 from '../images/intersect-test-loading-05.png';

// Import controls image
import tutorialPanel from '../images/tutorial-widget-v3.png';

// Import avatar selection images
import avatarIcon54 from '../images/intersect-avatar-icon-54.png';
import avatarIcon55 from '../images/intersect-avatar-icon-55.png';
import avatarIcon56 from '../images/intersect-avatar-icon-56.png';
import avatarIcon57 from '../images/intersect-avatar-icon-57.png';
import avatarIcon58 from '../images/intersect-avatar-icon-58.png';
import avatarIcon59 from '../images/intersect-avatar-icon-59.png';
import avatarIcon60 from '../images/intersect-avatar-icon-60.png';
import avatarIcon61 from '../images/intersect-avatar-icon-61.png';
import avatarIcon62 from '../images/intersect-avatar-icon-62.png';
import avatarIcon63 from '../images/intersect-avatar-icon-63.png';

declare global {
  interface Window {
    createUnityInstance: any;
    unityInstance: any;
    sendMessageToUnity: (gameObjectName: string, methodName: string, parameter?: string | number) => void;
    IVSBroadcastClient: typeof IVSBroadcastClient;
    /** When false, IVS stage audio uses legacy video-element output only. */
    __ffSpatialIvsAudioEnabled?: boolean;
    /** Muted state synced to Web Audio IVS path (see TemplateData/ivs.js). */
    __ffIvsStageMuted?: boolean;
    __ffIvsSpatial?: { setStageMuted: (muted: boolean) => void };
  }
}

// Expose IVS Broadcast Client globally for Unity/WebGL integration
(window as any).IVSBroadcastClient = IVSBroadcastClient;

// Unity message event type (detail is now just the payload string)
interface UnityMessageEvent extends CustomEvent {
  detail: string;
}

interface UnityGameProps {
  accessToken?: string;
  show?: any;
  ivsStageArn?: string;
  ivsParticipantToken?: string;
  ivsUserId?: string;
}

const loadScript = (src: string, type: 'module' | 'text/javascript' = 'text/javascript') => {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.type = type;
    script.onload = () => resolve();
    script.onerror = () => reject(`Failed to load script: ${src}`);
    document.body.appendChild(script);
  });
};

// Loading images array
const loadingImages = [
  intersectLoadingImage01,
  intersectLoadingImage02,
  intersectLoadingImage03,
  intersectLoadingImage04,
  intersectLoadingImage05
];

// ========================================
// CONFIGURATION - Easy to modify defaults
// ========================================
const DEFAULT_PANEL_ENABLED = true;   // Set to true to show panel on load, false to start minimized

const GRAPHICS_QUALITY_STORAGE_KEY = 'unity-graphics-quality';
const GRAPHICS_QUALITY_AUTO_DETECTED_KEY = 'unity-graphics-quality-auto-detected';
const QUALITY_SCALE: Record<Exclude<GraphicsQuality, 'auto'>, number> = {
  high: 1,
  medium: 0.75,
  low: 0.5,
  performance: 0.33
};
const MIN_CANVAS_WIDTH = 320;
const MIN_CANVAS_HEIGHT = 180;
const FPS_SAMPLE_DURATION_MS = 6000;
const FPS_THRESHOLD = 28;

/** Breakout spaces that show the shared guidelines modal on enter (teleport, area trigger, etc.). */
const BREAKOUT_GUIDELINES_ROOM_IDS = new Set([
  'intersect-room-01',
  'intersect-room-02',
  'intersect-room-03',
  'intersect-room-04',
]);

/** Unity CustomInteractable ids that show the breakout guidelines popup (not NPCDialog). */
const BREAKOUT_GUIDELINES_UNITY_NPC_IDS = new Set([
  'npc-breakout-room-1',
  'breakout-room-1-session',
  'npc-breakout-room-2',
  'breakout-room-2-session',
  'npc-breakout-room-3',
  'breakout-room-3-session',
  'npc-breakout-room-4',
  'breakout-room-4-session',
]);

function getStoredGraphicsQuality(): GraphicsQuality {
  try {
    const s = localStorage.getItem(GRAPHICS_QUALITY_STORAGE_KEY);
    if (s === 'auto' || s === 'high' || s === 'medium' || s === 'low' || s === 'performance') return s;
  } catch (_) {}
  return 'auto';
}

function getStoredAutoDetectedQuality(): GraphicsQuality | null {
  try {
    const s = localStorage.getItem(GRAPHICS_QUALITY_AUTO_DETECTED_KEY);
    if (s === 'high' || s === 'medium' || s === 'low' || s === 'performance') return s;
  } catch (_) {}
  return null;
}

function getEffectiveScale(quality: GraphicsQuality, autoDetected: GraphicsQuality | null): number {
  if (quality !== 'auto') return QUALITY_SCALE[quality];
  if (autoDetected && autoDetected !== 'auto') return QUALITY_SCALE[autoDetected];
  return QUALITY_SCALE.high;
}

const UnityGameInner: React.FC<UnityGameProps> = ({ accessToken, show, ivsStageArn, ivsParticipantToken, ivsUserId }) => {
  const { hasActiveOverlay } = useFocus();
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageOpacity, setImageOpacity] = useState(1);
  const [showControlsPopup, setShowControlsPopup] = useState(false);
  const [showAvatarSelection, setShowAvatarSelection] = useState(true); // Avatar selection modal
  const [showOnboarding, setShowOnboarding] = useState(false); // Onboarding tour
  const [isCustomizingAvatar, setIsCustomizingAvatar] = useState(false); // Loading state for avatar customization
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isIVSPanelCollapsed, setIsIVSPanelCollapsed] = useState(true); // true = minimized to dock Video tile
  const [isIVSPanelEnabled, setIsIVSPanelEnabled] = useState(DEFAULT_PANEL_ENABLED);
  const [isIntersectStaff, setIsIntersectStaff] = useState(false);
  const [isUnityWorldReady, setIsUnityWorldReady] = useState(false); // Track Unity world loaded state
  const [isUIVisible, setIsUIVisible] = useState(true); // Track UI visibility (toggle with H key)
  const [isAtPodium, setIsAtPodium] = useState(false); // Track if user is at podium
  const [hasScreenSharePermission, setHasScreenSharePermission] = useState(false); // Track if user has been granted screen share permission
  const screenShareAutoGrantedForRoom = useRef<string | null>(null); // Track which room auto-granted screenshare
  
  // Room state
  const [currentRoomId, setCurrentRoomId] = useState<string>('intersect-main');
  const currentRoomIdRef = useRef<string>('intersect-main'); // Ref for immediate synchronous access
  const [currentRoomName, setCurrentRoomName] = useState<string>('Intersect Main Room');
  const [chatRoomArn, setChatRoomArn] = useState<string>('');
  const [stageArnFromRoom, setStageArnFromRoom] = useState<string>('');
  const [stagePlaybackUrl, setStagePlaybackUrl] = useState<string>('');
  const [podiumPosition, setPodiumPosition] = useState<string>('');
  const [participantTokenFromRoom, setParticipantTokenFromRoom] = useState<string>('');
  const [userIdFromRoom, setUserIdFromRoom] = useState<string>('');
  /** Human-readable name for chat hand-raise payloads (same label as People / profile). */
  const [handRaiseDisplayName, setHandRaiseDisplayName] = useState<string | undefined>(undefined);
  const [isIntersectStaffFromRoom, setIsIntersectStaffFromRoom] = useState<boolean>(false);
  const [eventId] = useState<string>('22222');
  const [isRoomTransitioning, setIsRoomTransitioning] = useState<boolean>(false);
  
  // Room cache - store all rooms at startup for fast access
  const roomCacheRef = useRef<{ [key: string]: any }>({});
  const [isLoadingRooms, setIsLoadingRooms] = useState<boolean>(true);
  
  // Stage view mode
  const [isStageViewMode, setIsStageViewMode] = useState(false);
  const [isStageMuted, setIsStageMuted] = useState(true); // Stage audio muted by default
  const [isLocalUserScreenSharing, setIsLocalUserScreenSharing] = useState(false); // Mute stage playback when presenting to avoid double audio

  // IVS Web Audio: use isStageMuted only (screen share still mutes HLS StagePlayer via its isMuted prop).
  useEffect(() => {
    window.__ffIvsStageMuted = isStageMuted;
    window.__ffIvsSpatial?.setStageMuted(isStageMuted);
  }, [isStageMuted]);
  
  // Low resolution mode for resource saving (presenter mode: small window for screen share)
  const [isLowResMode, setIsLowResMode] = useState(false);

  // Graphics quality: render scale while filling screen (separate from presenter mode)
  const [graphicsQuality, setGraphicsQualityState] = useState<GraphicsQuality>(() => getStoredGraphicsQuality());
  const [autoDetectedQuality, setAutoDetectedQualityState] = useState<GraphicsQuality | null>(() => getStoredAutoDetectedQuality());

  const setGraphicsQuality = (q: GraphicsQuality) => {
    setGraphicsQualityState(q);
    try {
      localStorage.setItem(GRAPHICS_QUALITY_STORAGE_KEY, q);
    } catch (_) {}
  };

  const setAutoDetectedQuality = (q: GraphicsQuality | null) => {
    setAutoDetectedQualityState(q);
    try {
      if (q) localStorage.setItem(GRAPHICS_QUALITY_AUTO_DETECTED_KEY, q);
      else localStorage.removeItem(GRAPHICS_QUALITY_AUTO_DETECTED_KEY);
    } catch (_) {}
  };
  
  // Presenter mode - when user is at podium and in low-res mode (focused for screensharing)
  const isPresenterMode = isAtPodium && isLowResMode;
  
  // Fall detection and respawn
  const [showRespawnPopup, setShowRespawnPopup] = useState(false);
  const [showRespawnBanner, setShowRespawnBanner] = useState(false);
  const lastYPositionRef = useRef<number | null>(null);
  const hasFallPopupShownRef = useRef<boolean>(false);
  
  // Custom interactable cooldown (prevent spam)
  const lastInteractableTimeRef = useRef<number>(0);
  const INTERACTABLE_COOLDOWN_MS = 2000; // 2 second cooldown
  /** When the web opens the breakout guidelines popup; skip duplicate Unity interactable in the same window. */
  const breakoutGuidelinesOpenedFromWebAtRef = useRef(0);
  const prevRoomIdForBreakoutGuidelinesRef = useRef(currentRoomId);
  /** True after entering a breakout guidelines room until we successfully open the modal (handles enter-before-Unity-ready). */
  const breakoutGuidelinesPendingForVisitRef = useRef(false);
  const BREAKOUT_GUIDELINES_UNITY_NPC_DEDUPE_MS = 6000;

  const [showBreakoutGuidelines, setShowBreakoutGuidelines] = useState(false);

  /** Unity name-tag click → slim People-style profile (name, staff, committees). */
  const [playerClickedPanel, setPlayerClickedPanel] = useState<{ userId: string; displayName: string } | null>(
    null
  );
  const [localVideoControlApi, setLocalVideoControlApi] = useState<LocalVideoControlsApi | null>(null);
  const [screenShareControlApi, setScreenShareControlApi] = useState<ScreenShareControlsApi | null>(null);
  const [videoDockPopoverOpen, setVideoDockPopoverOpen] = useState(false);
  const [screenShareDockPopoverOpen, setScreenShareDockPopoverOpen] = useState(false);
  const videoDockPopoverOwnerId = useMemo(() => `dock-popover-video-${Math.random().toString(36).slice(2)}`, []);
  const screenShareDockPopoverOwnerId = useMemo(
    () => `dock-popover-screenshare-${Math.random().toString(36).slice(2)}`,
    []
  );
  const videoDockTileRef = useRef<HTMLDivElement>(null);
  const screenShareDockTileRef = useRef<HTMLDivElement>(null);
  const videoCameraPreviewHostRef = useRef<HTMLDivElement>(null);
  const localVideoControlApiRef = useRef<LocalVideoControlsApi | null>(null);
  /** One auto-open preview per Video popover open; reset when popover closes. */
  const videoPreviewOpenedForPopoverRef = useRef(false);

  localVideoControlApiRef.current = localVideoControlApi;

  const handleScreenShareTutorialRequest = useCallback(() => {
    setScreenShareDockPopoverOpen(true);
  }, []);

  /** Face pin (self) → same prep as clicking the Video dock tile, then open the popover. */
  const openVideoDockFromFacePin = useCallback(() => {
    if (!isIVSPanelEnabled) {
      logger.log('[IVS Panel] Re-enabling panel and rejoining stream');
      setIsIVSPanelEnabled(true);
    }
    setIsIVSPanelCollapsed(true);
    setScreenShareDockPopoverOpen(false);
    setVideoDockPopoverOpen(true);
  }, [isIVSPanelEnabled]);

  // NPC Dialog state
  const [showNPCDialog, setShowNPCDialog] = useState(false);
  const [currentNPCId, setCurrentNPCId] = useState<string>('');
  const [currentNPCMessageIndex, setCurrentNPCMessageIndex] = useState(0);

  const [handRaiseByUserId, setHandRaiseByUserId] = useState<
    Record<string, { displayName: string; raisedAt: number }>
  >({});

  const [clapBursts, setClapBursts] = useState<
    Array<{ id: string; displayName: string; leftPct: number; topPct: number; dx: string; dy: string }>
  >([]);

  const handleHandRaiseSignal = useCallback((e: HandRaiseSignal) => {
    if (e.raised) {
      setHandRaiseByUserId((prev) => ({
        ...prev,
        [e.userId]: { displayName: e.displayName, raisedAt: e.at },
      }));
    } else {
      setHandRaiseByUserId((prev) => {
        if (!(e.userId in prev)) return prev;
        const next = { ...prev };
        delete next[e.userId];
        return next;
      });
    }
  }, []);

  const handleClapSignal = useCallback((e: ClapSignal) => {
    const id = `${e.at}-${Math.random().toString(36).slice(2, 10)}`;
    const leftPct = 8 + Math.random() * 78;
    const topPct = 12 + Math.random() * 58;
    const dx = `${(Math.random() - 0.5) * 200}px`;
    const dy = `${-70 - Math.random() * 100}px`;

    setClapBursts((prev) => [...prev, { id, displayName: e.displayName, leftPct, topPct, dx, dy }]);
    window.setTimeout(() => {
      setClapBursts((prev) => prev.filter((b) => b.id !== id));
    }, 3200);
  }, []);

  const handRaiseNotificationItems = useMemo(
    () =>
      Object.entries(handRaiseByUserId).map(([userId, v]) => ({
        userId,
        displayName: v.displayName,
        raisedAt: v.raisedAt,
      })),
    [handRaiseByUserId]
  );

  const localUserIdsForHandRaise = useMemo(() => {
    const a = userIdFromRoom != null ? String(userIdFromRoom).trim() : '';
    const b = ivsUserId != null ? String(ivsUserId).trim() : '';
    const out: string[] = [];
    if (a) out.push(a);
    if (b && b !== a) out.push(b);
    return out;
  }, [userIdFromRoom, ivsUserId]);

  const isLocalHandRaisedInQueue = useMemo(() => {
    return localUserIdsForHandRaise.some((id) =>
      Object.prototype.hasOwnProperty.call(handRaiseByUserId, id)
    );
  }, [handRaiseByUserId, localUserIdsForHandRaise]);

  const handleModeratorUnraiseHand = useCallback(async (targetUserId: string) => {
    const chat = chatRef.current;
    if (!chat?.sendModeratorUnraiseHand) {
      throw new Error('Chat unavailable');
    }
    await chat.sendModeratorUnraiseHand(targetUserId);
  }, []);

  useEffect(() => {
    setHandRaiseByUserId({});
    setClapBursts([]);
  }, [currentRoomId]);

  // Room presence (DynamoDB heartbeats) — People widget is decoupled from IVS/video-call
  useEffect(() => {
    if (!accessToken || !eventId || !currentRoomId) return;
    const pathParams = { eventId, roomId: currentRoomId };
    const presenceUrl = `${process.env.REACT_APP_API_URL}/events/${encodeURIComponent(
      eventId
    )}/rooms/${encodeURIComponent(currentRoomId)}/presence`;
    const heartbeat = () => {
      API.postRoomPresence(accessToken, pathParams).catch(() => {});
    };
    heartbeat();
    const interval = window.setInterval(heartbeat, 35000);

    const leavePresenceKeepalive = () => {
      fetch(presenceUrl, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
        keepalive: true,
      }).catch(() => {});
    };
    const onPageHide = () => leavePresenceKeepalive();
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      clearInterval(interval);
      API.deleteRoomPresence(accessToken, pathParams).catch(() => {});
    };
  }, [accessToken, eventId, currentRoomId]);

  // Area trigger interaction state
  const [showAreaTriggerPrompt, setShowAreaTriggerPrompt] = useState(false);
  const [pendingAreaTriggerRoom, setPendingAreaTriggerRoom] = useState<any>(null);

  // Private room gate: shared unlocked codes and modal for "enter code to join"
  const [unlockedCodes, setUnlockedCodes] = useState<string[]>([]);
  const [privateRoomGateRoom, setPrivateRoomGateRoom] = useState<any>(null);
  const [privateRoomGateTitle, setPrivateRoomGateTitle] = useState<string>('');
  const [privateRoomGateCodeInput, setPrivateRoomGateCodeInput] = useState('');
  const [privateRoomGateError, setPrivateRoomGateError] = useState<string | null>(null);
  const [privateRoomGateSubmitting, setPrivateRoomGateSubmitting] = useState(false);
  
  // NPC dialogue messages - map NPC IDs to their specific dialogues
  const npcDialogues: { [key: string]: string[] } = {
    'npc-1': ["Don't forget to check out the stage presentations!"],
    'npc-2': ["Have you checked what's happening today?"],
    'npc-3': ["Remember, you can ask staff for help at any time."],
    'npc-4': ["Enjoy the event and make the most of your experience!"],
    'npc-5': ["Welcome! Feel free to explore all the breakout rooms."],
    'npc-6': [
      "Welcome to the Intersect Virtual Hub!",
      "Feel free to ask staff for help at any time.",
    ],
  };

  // Register modals as overlays to prevent Unity from recapturing input
  useOverlay('controls-popup', showControlsPopup);
  useOverlay('avatar-selection', showAvatarSelection);
  useOverlay('respawn-popup', showRespawnPopup);
  useOverlay('npc-dialog', showNPCDialog);
  useOverlay('breakout-guidelines', showBreakoutGuidelines);
  useOverlay('private-room-gate', !!privateRoomGateRoom);
  useOverlay('player-clicked-profile', !!playerClickedPanel);
  useOverlay('unity-dock-video-popover', videoDockPopoverOpen);
  useOverlay('unity-dock-screenshare-popover', screenShareDockPopoverOpen);

  useEffect(() => {
    if (!videoDockPopoverOpen && !screenShareDockPopoverOpen) {
      return undefined;
    }
    const onMouseDown = (event: MouseEvent) => {
      const t = event.target as Node;
      const inVideoTile = videoDockTileRef.current?.contains(t);
      const inVideoPopover = (t as Element).closest?.(`[data-unity-dock-popover-owner="${videoDockPopoverOwnerId}"]`);
      const inShareTile = screenShareDockTileRef.current?.contains(t);
      const inSharePopover = (t as Element).closest?.(
        `[data-unity-dock-popover-owner="${screenShareDockPopoverOwnerId}"]`
      );
      const onLocalFacePin = (t as Element).closest?.('[data-ff-local-face-pin]');
      if (videoDockPopoverOpen && !inVideoTile && !inVideoPopover && !onLocalFacePin) {
        localVideoControlApiRef.current?.closeCameraPreview?.();
        setVideoDockPopoverOpen(false);
      }
      if (screenShareDockPopoverOpen && !inShareTile && !inSharePopover) {
        setScreenShareDockPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [
    videoDockPopoverOpen,
    screenShareDockPopoverOpen,
    videoDockPopoverOwnerId,
    screenShareDockPopoverOwnerId,
  ]);

  useEffect(() => {
    if (!videoDockPopoverOpen && !screenShareDockPopoverOpen) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        localVideoControlApiRef.current?.closeCameraPreview?.();
        setVideoDockPopoverOpen(false);
        setScreenShareDockPopoverOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [videoDockPopoverOpen, screenShareDockPopoverOpen]);

  useLayoutEffect(() => {
    if (!videoDockPopoverOpen) {
      videoPreviewOpenedForPopoverRef.current = false;
      return;
    }
    if (videoPreviewOpenedForPopoverRef.current) {
      return;
    }
    const api = localVideoControlApiRef.current;
    // Preview uses local getUserMedia only — room video-call token / stage is not required.
    if (!api || api.isCameraPreviewOpen) {
      return;
    }
    // If camera is already on when the popover opens, we skip auto-preview but must still mark this session so
    // turning the camera off does not auto-relaunch preview (ref would otherwise stay false).
    if (api.localVideoEnabled) {
      videoPreviewOpenedForPopoverRef.current = true;
      return;
    }
    videoPreviewOpenedForPopoverRef.current = true;
    void api.openCameraPreview();
  }, [videoDockPopoverOpen, localVideoControlApi]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const unityInstanceRef = useRef<any>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<IVSRealtimeChatRef | null>(null);
  const roomSelectorRef = useRef<RoomSelectorRef>(null);
  const accessTokenRef = useRef<string | undefined>(accessToken);
  const localViewerUserIdRef = useRef('');

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    const a = (userIdFromRoom || '').trim();
    const b = (ivsUserId || '').trim();
    localViewerUserIdRef.current = a || b;
  }, [userIdFromRoom, ivsUserId]);

  // Analytics tracking
  const analytics = useAnalytics({
    accessToken: accessToken || null,
    eventId,
    platform: 'webgpu',
    buildVersion: '1.0.0'
  });

  // VV web session tracking for current_users / analytics (same as vv-web-app)
  useWebSession({
    accessToken: accessToken || null,
    eventId,
    enabled: !!(accessToken && eventId),
  });

  // Ref to store analytics functions for use in event handlers
  const analyticsRef = useRef(analytics);
  useEffect(() => {
    analyticsRef.current = analytics;
  }, [analytics]);

  const customizeActiveAvatar = async (avatarId: string, characterName: string) => {
    if (!accessToken || isCustomizingAvatar) return; // Prevent multiple clicks

    setIsCustomizingAvatar(true);
    logger.log(`[Avatar Selection] Customizing avatar: ${characterName}`);

    try {
      const response = await API.customizeActiveAvatarIntersect(accessToken, {
        avatar_id: characterName,
        event_id: eventId
      });
      
      if (response.status !== 200 && response.status !== 204) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('[Avatar Selection] Failed to customize avatar:', errorText);
        setIsCustomizingAvatar(false);
        window.alert(`Failed to customize avatar. Please try again.\n\nError: ${errorText}`);
        return;
      }
      
      logger.log('[Avatar Selection] Successfully customized avatar');
      // Close avatar selection modal after successful selection
      setShowAvatarSelection(false);
      setIsCustomizingAvatar(false);
    } catch (error) {
      console.error('[Avatar Selection] Error customizing avatar:', error);
      setIsCustomizingAvatar(false);
      window.alert('Failed to customize avatar. Please check your connection and try again.');
    }
  };

  const avatarOptions = [
    { id: '54', characterName: 'IntersectCharacter1', image: avatarIcon54 },
    { id: '55', characterName: 'IntersectCharacter2', image: avatarIcon55 },
    { id: '56', characterName: 'IntersectCharacter3', image: avatarIcon56 },
    { id: '57', characterName: 'IntersectCharacter4', image: avatarIcon57 },
    { id: '58', characterName: 'IntersectCharacter5', image: avatarIcon58 },
    { id: '59', characterName: 'IntersectCharacter6', image: avatarIcon59 },
    { id: '60', characterName: 'IntersectCharacter7', image: avatarIcon60 },
    { id: '61', characterName: 'IntersectCharacter8', image: avatarIcon61 },
    { id: '62', characterName: 'IntersectCharacter9', image: avatarIcon62 },
    { id: '63', characterName: 'IntersectCharacter10', image: avatarIcon63 },
  ];

  const handleControlsClick = () => {
    setShowControlsPopup(true);
  };

  const handlePopupClose = () => {
    setShowControlsPopup(false);
    // FocusContext automatically re-enables Unity capture when overlay is unregistered
  };

  // NPC Dialog handlers
  const handleNPCNext = () => {
    const currentMessages = npcDialogues[currentNPCId] || [];
    setCurrentNPCMessageIndex((prev) => {
      const nextIndex = prev + 1;
      if (nextIndex >= currentMessages.length) {
        // If we've reached the end, close the dialog
        setShowNPCDialog(false);
        return 0;
      }
      return nextIndex;
    });
  };

  const handleNPCClose = () => {
    setShowNPCDialog(false);
    setCurrentNPCMessageIndex(0);
  };

  // Breakout guidelines popup when entering breakout rooms 1–4 (teleport, Agenda, area trigger, etc.)
  useEffect(() => {
    if (!BREAKOUT_GUIDELINES_ROOM_IDS.has(currentRoomId)) {
      breakoutGuidelinesPendingForVisitRef.current = false;
      prevRoomIdForBreakoutGuidelinesRef.current = currentRoomId;
      setShowBreakoutGuidelines(false);
      return;
    }

    const prev = prevRoomIdForBreakoutGuidelinesRef.current;
    prevRoomIdForBreakoutGuidelinesRef.current = currentRoomId;
    if (prev !== currentRoomId) {
      breakoutGuidelinesPendingForVisitRef.current = true;
    }

    if (!isUnityWorldReady || showAvatarSelection) return;
    if (!breakoutGuidelinesPendingForVisitRef.current) return;
    breakoutGuidelinesPendingForVisitRef.current = false;

    breakoutGuidelinesOpenedFromWebAtRef.current = Date.now();
    setShowBreakoutGuidelines(true);
  }, [currentRoomId, isUnityWorldReady, showAvatarSelection]);

  // Handle click on Unity canvas to re-enable capture
  const handleCanvasClick = () => {
    // Don't recapture if a UI overlay is active
    if (hasActiveOverlay) return;
    
    // logger.log('[Browser→Unity] Canvas clicked - re-enabling Unity capture');
    if (window.sendMessageToUnity) {
      try {
        // Enable keyboard capture
        const captureKeyboardData = {
          eventType: 'CaptureKeyboard',
          payload: JSON.stringify({ capture: true })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
        // logger.log('[Browser→Unity] Enabled keyboard capture');

        // Enable cursor capture
        const captureCursorData = {
          eventType: 'CaptureCursor',
          payload: JSON.stringify({ capture: true })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
        // logger.log('[Browser→Unity] Enabled cursor capture');
      } catch (error) {
        console.error('[Browser→Unity] Error enabling Unity capture:', error);
      }
    }
  };

  // Handle right-click on Unity canvas to re-enable capture
  const handleCanvasRightClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent context menu
    // logger.log('[Browser→Unity] Canvas right-clicked - re-enabling Unity capture');
    if (window.sendMessageToUnity) {
      try {
        // Enable keyboard capture
        const captureKeyboardData = {
          eventType: 'CaptureKeyboard',
          payload: JSON.stringify({ capture: true })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
        // logger.log('[Browser→Unity] Enabled keyboard capture');

        // Enable cursor capture
        const captureCursorData = {
          eventType: 'CaptureCursor',
          payload: JSON.stringify({ capture: true })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
        // logger.log('[Browser→Unity] Enabled cursor capture');
      } catch (error) {
        console.error('[Browser→Unity] Error enabling Unity capture:', error);
      }
    }
  };

  // Handle room change
  const handleRoomChange = (room: any, skipTeleport: boolean = false) => {
    logger.log('[Room Change] Switching to room:', room.room_id, room.name, skipTeleport ? '(no teleport)' : '(with teleport)');
    
    // Track room exit/enter for analytics (use ref for event handler compatibility)
    analyticsRef.current.enterRoom(room.room_id, room.name || room.room_name);
    logger.log('[Analytics] Room change tracked:', room.room_id);
    
    // Update ref immediately for synchronous access (before setTimeout)
    currentRoomIdRef.current = room.room_id;
    logger.log('[Room Change] Updated currentRoomIdRef to:', room.room_id);
    
    // Set transitioning state to show loading UI
    setIsRoomTransitioning(true);
    
    // Clear current state to force disconnect
    setChatRoomArn('');
    setStageArnFromRoom('');
    setStagePlaybackUrl('');
    setPodiumPosition('');
    setParticipantTokenFromRoom('');
    setUserIdFromRoom('');
    setIsIntersectStaffFromRoom(false);
    
    // Longer delay to ensure complete disconnect, then connect to new room
    setTimeout(() => {
      setCurrentRoomId(room.room_id);
      setCurrentRoomName(room.name);
      setChatRoomArn(room.chat_room_arn);
      setStageArnFromRoom(room.stage_arn);
      setStagePlaybackUrl(room.stage_playback_url || '');
      setPodiumPosition(room.podium_position || '');
      // Note: isRoomTransitioning will be cleared after participant token is fetched
      
      // Teleport player to room's spawn location (unless skipTeleport is true for area triggers)
      if (!skipTeleport && room.spawn_position && room.spawn_rotation) {
        // Parse position string "(x, y, z)" into object { x, y, z }
        const posMatch = room.spawn_position.match(/\(([^,]+),\s*([^,]+),\s*([^)]+)\)/);
        const rotMatch = room.spawn_rotation.match(/\(([^,]+),\s*([^,]+),\s*([^)]+)\)/);
        
        if (posMatch && rotMatch) {
          const position = {
            x: parseFloat(posMatch[1]),
            y: parseFloat(posMatch[2]),
            z: parseFloat(posMatch[3])
          };
          const rotation = {
            x: parseFloat(rotMatch[1]),
            y: parseFloat(rotMatch[2]),
            z: parseFloat(rotMatch[3])
          };
          
          const teleportData = {
            eventType: 'Teleport',
            payload: JSON.stringify({
              position,
              rotation
            })
          };
          sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
          logger.log('[Room Switch] Teleporting to:', position, rotation);
        } else {
          console.error('[Room Switch] Failed to parse spawn position or rotation:', room.spawn_position, room.spawn_rotation);
        }
      } else if (skipTeleport) {
        logger.log('[Room Switch] Skipping teleport for area trigger room change');
      }
      
      // participantTokenFromRoom, userIdFromRoom, and isIntersectStaffFromRoom will be fetched by the useEffect when currentRoomId updates
    }, 100);
  };

  // Gate room entry: if room has an active private booking, require code (or host bypass)
  const handleRoomChangeRequest = async (room: any, skipTeleport: boolean = false) => {
    if (!accessToken || !eventId) {
      handleRoomChange(room, skipTeleport);
      return;
    }
    try {
      const response = await API.getActivePrivateBookingForRoom(accessToken, eventId, room.room_id);
      if (response.status === 404) {
        handleRoomChange(room, skipTeleport);
        return;
      }
      if (response.status !== 200) {
        handleRoomChange(room, skipTeleport);
        return;
      }
      const booking = await response.json();
      const currentUserId = userIdFromRoom || ivsUserId || '';
      if (booking.booked_by_user_id && booking.booked_by_user_id === currentUserId) {
        handleRoomChange(room, skipTeleport);
        return;
      }
      setPrivateRoomGateRoom(room);
      setPrivateRoomGateTitle(booking.title || 'Private booking');
      setPrivateRoomGateCodeInput('');
      setPrivateRoomGateError(null);
    } catch {
      handleRoomChange(room, skipTeleport);
    }
  };

  const handlePrivateRoomGateSubmit = async () => {
    const room = privateRoomGateRoom;
    const code = privateRoomGateCodeInput.trim();
    if (!room || !code || !accessToken) return;
    setPrivateRoomGateError(null);
    setPrivateRoomGateSubmitting(true);
    try {
      const response = await API.getBookingByCode(accessToken, eventId, code);
      if (response.status === 200) {
        const booking = await response.json();
        if (booking.room_id === room.room_id) {
          setUnlockedCodes((prev) => (prev.includes(code.toUpperCase()) ? prev : [...prev, code.toUpperCase()]));
          setPrivateRoomGateRoom(null);
          setPrivateRoomGateTitle('');
          setPrivateRoomGateCodeInput('');
          handleRoomChange(room, false);
        } else {
          setPrivateRoomGateError('Invalid code for this room.');
        }
      } else {
        setPrivateRoomGateError('Invalid code.');
      }
    } catch {
      setPrivateRoomGateError('Something went wrong. Try again.');
    } finally {
      setPrivateRoomGateSubmitting(false);
    }
  };

  const handlePrivateRoomGateCancel = () => {
    setPrivateRoomGateRoom(null);
    setPrivateRoomGateTitle('');
    setPrivateRoomGateCodeInput('');
    setPrivateRoomGateError(null);
  };

  const handleStageViewToggle = (enabled: boolean) => {
    setIsStageViewMode(enabled);
    logger.log('[Stage View]', enabled ? 'Entering stage mode' : 'Exiting stage mode');
  };

  // Handle respawn confirmation
  const handleRespawn = async () => {
    logger.log('[Unity→Browser] Respawning player to spawn location');
    
    // Check if in a breakout room - if so, return to main room first
    const isInBreakoutRoom = currentRoomId !== 'intersect-main';
    
    if (isInBreakoutRoom) {
      logger.log('[Unity→Browser] Player is in breakout room, returning to main room first');
      
      // Get main room from cache
      const mainRoom = roomCacheRef.current['intersect-main'];
      
      if (mainRoom) {
        logger.log('[Unity→Browser] Switching to main room before respawn');
        handleRoomChange(mainRoom); // This will teleport to main room spawn
      } else {
        logger.warn('[Unity→Browser] Main room not in cache, teleporting to default spawn');
        try {
          const teleportData = {
            eventType: 'Teleport',
            payload: JSON.stringify({
              position: { x: 0, y: 0.01, z: 0 },
              rotation: { x: 0, y: 180, z: 0 }
            })
          };
          sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
          logger.log('[Browser→Unity] Teleport command sent to default spawn');
        } catch (error) {
          console.error('[Browser→Unity] Error sending teleport command:', error);
        }
      }
    } else {
      // Already in main room, just teleport to spawn
      logger.log('[Unity→Browser] Already in main room, teleporting to spawn');
      try {
        const teleportData = {
          eventType: 'Teleport',
          payload: JSON.stringify({
            position: { x: 0, y: 0.01, z: 0 },
            rotation: { x: 0, y: 180, z: 0 }
          })
        };
        sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
        logger.log('[Browser→Unity] Teleport command sent');
      } catch (error) {
        console.error('[Browser→Unity] Error sending teleport command:', error);
      }
    }
    
    // Close popup and banner, reset fall detection
    setShowRespawnPopup(false);
    setShowRespawnBanner(false);
    hasFallPopupShownRef.current = false;
    lastYPositionRef.current = null; // Reset to re-initialize stable Y on next update
  };

  const handleRespawnCancel = () => {
    setShowRespawnPopup(false);
    // Show persistent banner instead
    setShowRespawnBanner(true);
  };

  // Handle leaving podium - teleport back to spawn point
  const handleLeavePodium = () => {
    logger.log('[Unity→Browser] Leaving podium, returning to spawn point');
    
    // Get current room from cache
    const currentRoom = roomCacheRef.current[currentRoomId];
    
    if (currentRoom && currentRoom.spawn_position && currentRoom.spawn_rotation) {
      // Parse position string "(x, y, z)" into object { x, y, z }
      const posMatch = currentRoom.spawn_position.match(/\(([^,]+),\s*([^,]+),\s*([^)]+)\)/);
      const rotMatch = currentRoom.spawn_rotation.match(/\(([^,]+),\s*([^,]+),\s*([^)]+)\)/);
      
      if (posMatch && rotMatch) {
        const position = {
          x: parseFloat(posMatch[1]),
          y: parseFloat(posMatch[2]),
          z: parseFloat(posMatch[3])
        };
        const rotation = {
          x: parseFloat(rotMatch[1]),
          y: parseFloat(rotMatch[2]),
          z: parseFloat(rotMatch[3])
        };
        
        const teleportData = {
          eventType: 'Teleport',
          payload: JSON.stringify({
            position,
            rotation
          })
        };
        sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
        logger.log('[Browser→Unity] Teleporting back to spawn:', position, rotation);
        
        // Clear podium state and screen share permission
        setIsAtPodium(false);
        setHasScreenSharePermission(false);
      } else {
        console.error('[Unity→Browser] Failed to parse spawn position or rotation:', currentRoom.spawn_position, currentRoom.spawn_rotation);
      }
    } else {
      // Fallback to default spawn if no room data
      try {
        const teleportData = {
          eventType: 'Teleport',
          payload: JSON.stringify({
            position: { x: 0, y: 0.01, z: 0 },
            rotation: { x: 0, y: 180, z: 0 }
          })
        };
        sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));
        logger.log('[Browser→Unity] Teleported to default spawn');
        setIsAtPodium(false);
        setHasScreenSharePermission(false);
      } catch (error) {
        console.error('[Browser→Unity] Error sending teleport command:', error);
      }
    }
  };

  // Handle screen share state changes - auto-enable low-res mode when sharing from podium
  const handleScreenShareChange = (isSharing: boolean) => {
    logger.log('[Unity] Screen share state changed:', isSharing);
    setIsLocalUserScreenSharing(isSharing); // Mute stage playback when presenting to avoid double audio
    if (isSharing && isAtPodium) {
      logger.log('[Unity] Auto-enabling low-res mode for podium presenter');
      setIsLowResMode(true);
      // Set canvas resolution for resource savings
      if (canvasRef.current) {
        canvasRef.current.width = 640;
        canvasRef.current.height = 360;
      }
    }
  };

  // Handle toggling presentation mode (low-res mode for screen sharing)
  const handleTogglePresentationMode = () => {
    const newLowResMode = !isLowResMode;
    logger.log('[Unity] Toggling presentation mode:', newLowResMode);
    setIsLowResMode(newLowResMode);

    if (canvasRef.current) {
      if (newLowResMode) {
        canvasRef.current.width = 640;
        canvasRef.current.height = 360;
      } else {
        // Resolution restored by useEffect (quality-based scale) when isLowResMode becomes false
        setTimeout(() => {
          chatRef.current?.scrollToBottom();
        }, 100);
      }
    }
  };

  // Bridge function to send messages to Unity
  const sendMessageToUnity = (gameObjectName: string, methodName: string, parameter?: string | number) => {
    if (unityInstanceRef.current) {
      logger.log(`Sending to Unity: GameObject="${gameObjectName}", Method="${methodName}", Param="${parameter}"`);
      try {
        if (parameter !== undefined) {
          unityInstanceRef.current.SendMessage(gameObjectName, methodName, parameter);
        } else {
          unityInstanceRef.current.SendMessage(gameObjectName, methodName);
        }
        logger.log('Message sent successfully to Unity');
      } catch (error) {
        console.error('Error sending message to Unity:', error);
      }
    } else {
      logger.warn('Unity instance not ready yet');
    }
  };

  const handleFullscreenClick = async () => {
    if (!gameContainerRef.current) return;

    try {
      if (!isFullscreen) {
        // Enter fullscreen - use the game container so widgets are included
        if (gameContainerRef.current.requestFullscreen) {
          await gameContainerRef.current.requestFullscreen();
        } else if ((gameContainerRef.current as any).webkitRequestFullscreen) {
          await (gameContainerRef.current as any).webkitRequestFullscreen();
        } else if ((gameContainerRef.current as any).mozRequestFullScreen) {
          await (gameContainerRef.current as any).mozRequestFullScreen();
        } else if ((gameContainerRef.current as any).msRequestFullscreen) {
          await (gameContainerRef.current as any).msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Apply graphics quality resolution (fill screen): set canvas internal size to container * scale when not in presenter mode
  useEffect(() => {
    if (isLowResMode) return;
    const container = gameContainerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas || !unityInstanceRef.current) return;

    const applyResolution = () => {
      const w = container.clientWidth || window.innerWidth;
      const h = container.clientHeight || window.innerHeight;
      if (w <= 0 || h <= 0) return;
      const scale = getEffectiveScale(graphicsQuality, autoDetectedQuality);
      const cw = Math.max(MIN_CANVAS_WIDTH, Math.floor(w * scale));
      const ch = Math.max(MIN_CANVAS_HEIGHT, Math.floor(h * scale));
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
        logger.log('[Unity] Canvas resolution set to', cw, 'x', ch, '(scale', scale, ')');
      }
    };

    applyResolution();
    const ro = new ResizeObserver(() => applyResolution());
    ro.observe(container);
    return () => ro.disconnect();
  }, [isLowResMode, graphicsQuality, autoDetectedQuality, isLoading]);

  // Auto-detect slow performance and set autoDetectedQuality (used when graphicsQuality === 'auto')
  useEffect(() => {
    if (!isUnityWorldReady) return;
    const deltas: number[] = [];
    let lastTime = performance.now();
    let rafId: number;
    const timeoutId = window.setTimeout(() => {
      cancelAnimationFrame(rafId);
      if (deltas.length < 10) return;
      const avgMs = deltas.reduce((a, b) => a + b, 0) / deltas.length;
      const avgFps = 1000 / avgMs;
      if (avgFps < FPS_THRESHOLD) {
        const suggested: GraphicsQuality = avgFps < 18 ? 'low' : 'medium';
        logger.log('[Unity] Low FPS detected:', avgFps.toFixed(1), '- suggesting quality:', suggested);
        setAutoDetectedQuality(suggested);
      }
    }, FPS_SAMPLE_DURATION_MS);

    const tick = () => {
      const now = performance.now();
      deltas.push(now - lastTime);
      lastTime = now;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      window.clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, [isUnityWorldReady]);

  // Listen for Ctrl+H to toggle UI visibility (hidden feature for all users)
  // Listen for E key to interact with area triggers
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && (event.key === 'h' || event.key === 'H')) {
        event.preventDefault(); // Prevent browser's default Ctrl+H behavior
        logger.log('[Unity→Browser] Ctrl+H - toggling UI visibility');
        setIsUIVisible((prev) => !prev);
      }
      
      // E key to interact with area trigger
      if ((event.key === 'e' || event.key === 'E') && showAreaTriggerPrompt && pendingAreaTriggerRoom) {
        event.preventDefault();
        logger.log('[Unity→Browser] E key - triggering room change from area trigger');
        handleRoomChangeRequest(pendingAreaTriggerRoom, false);
        setShowAreaTriggerPrompt(false);
        setPendingAreaTriggerRoom(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAreaTriggerPrompt, pendingAreaTriggerRoom]);

  // Listen for messages from Unity
  useEffect(() => {
    // Generic Unity message handler
    const handleUnityMessage = (event: Event) => {
      const unityEvent = event as UnityMessageEvent;
      const eventType = event.type; // Event name (e.g., 'unityMessage')
      const payload = unityEvent.detail; // Payload string
      
      logger.log('[Unity→Browser] Received event:', {
        type: eventType,
        payload: payload
      });

      try {
        // Try to parse payload as JSON
        const data = JSON.parse(payload);
        // logger.log('[Unity→Browser] Parsed JSON data:', data);
        
        // Handle different message types
        const messageType = data.type || eventType;
        
        switch (messageType) {
          case 'chatMessage':
            // logger.log('[Unity→Browser] Chat message:', data.message || data);
            // TODO: Forward to chat component if needed
            break;
          case 'playerJoined':
            // logger.log('[Unity→Browser] Player joined:', data.playerName || data);
            break;
          case 'gameEvent':
            // logger.log('[Unity→Browser] Game event:', data.eventName || data);
            break;
          case 'message':
          case 'unityMessage':
            // Check if this is a terminal event
            if (data.id === 'terminal' || data?.id?.toLowerCase?.() === 'terminal') {
              logger.log('[Unity→Browser] Opening Room Selector from unityMessage terminal');
              if (roomSelectorRef.current) {
                roomSelectorRef.current.open();
              }
            }
            break;
          default:
            // Check if this is a terminal event
            if (data.id === 'terminal' || data?.id?.toLowerCase?.() === 'terminal') {
              logger.log('[Unity→Browser] Opening Room Selector from terminal (default case)');
              if (roomSelectorRef.current) {
                roomSelectorRef.current.open();
              }
            }
        }
      } catch (e) {
        // Payload is not JSON, treat as plain string
        logger.log('[Unity→Browser] Plain text message:', payload);
      }
    };

    // Release Unity's keyboard/cursor capture
    const releaseUnityCapture = () => {
      try {
        const captureKeyboardData = {
          eventType: 'CaptureKeyboard',
          payload: JSON.stringify({ capture: false })
        };
        sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
        logger.log('[Browser→Unity] Released keyboard capture');
      } catch (error) {
        console.error('[Browser→Unity] Error releasing keyboard capture:', error);
      }

      try {
        const captureCursorData = {
          eventType: 'CaptureCursor',
          payload: JSON.stringify({ capture: false })
        };
        sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
        logger.log('[Browser→Unity] Released cursor capture');
      } catch (error) {
        console.error('[Browser→Unity] Error releasing cursor capture:', error);
      }
    };

    // Key action handlers
    const keyActions: Record<string, () => void> = {
      // Return/Enter - Open chat
      'Return': () => {
        logger.log('[Unity→Browser] Return/Enter key - opening chat');
        releaseUnityCapture();
        if (chatRef.current && chatRef.current.focusInput) {
          chatRef.current.focusInput();
        }
      },
      'Enter': () => keyActions['Return'](),
      
      // C - TODO: Define action
      'C': () => {
        logger.log('[Unity→Browser] C key pressed - action not implemented yet');
        // TODO: Add C key functionality
      },
      
      // R - Show respawn confirmation popup
      'R': () => {
        logger.log('[Unity→Browser] R key - showing respawn confirmation');
        setShowRespawnPopup(true);
        setShowRespawnBanner(false); // Hide banner when popup is shown
        hasFallPopupShownRef.current = false; // Allow fall detection to trigger again if needed
      },
      
      // M - TODO: Define action
      'M': () => {
        logger.log('[Unity→Browser] M key pressed - action not implemented yet');
        // TODO: Add M key functionality
      },
      
      // Tab - TODO: Define action
      'Tab': () => {
        logger.log('[Unity→Browser] Tab key pressed - action not implemented yet');
        // TODO: Add Tab key functionality
      },
      
      // Q - TODO: Define action
      'Q': () => {
        logger.log('[Unity→Browser] Q key pressed - action not implemented yet');
        // TODO: Add Q key functionality
      },
      
      // T - TODO: Define action (maybe also open chat?)
      'T': () => {
        logger.log('[Unity→Browser] T key pressed - action not implemented yet');
        // TODO: Add T key functionality
        // Option: Could also open chat like Return
        // releaseUnityCapture();
        // if (chatRef.current && chatRef.current.focusInput) {
        //   chatRef.current.focusInput();
        // }
      },
      
      
      // Number keys 0-9 - TODO: Define actions
      '0': () => {
        logger.log('[Unity→Browser] 0 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '1': () => {
        logger.log('[Unity→Browser] 1 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '2': () => {
        logger.log('[Unity→Browser] 2 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '3': () => {
        logger.log('[Unity→Browser] 3 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '4': () => {
        logger.log('[Unity→Browser] 4 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '5': () => {
        logger.log('[Unity→Browser] 5 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '6': () => {
        logger.log('[Unity→Browser] 6 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '7': () => {
        logger.log('[Unity→Browser] 7 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '8': () => {
        logger.log('[Unity→Browser] 8 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      },
      '9': () => {
        logger.log('[Unity→Browser] 9 key pressed - action not implemented yet');
        // TODO: Add number key functionality
      }
    };

    // Keyboard input handler (specific event type)
    const handleKeyboardInput = (event: Event) => {
      const unityEvent = event as UnityMessageEvent;
      const payload = unityEvent.detail;
      
      logger.log('[Unity→Browser] Keyboard input received:', payload);
      
      let keyCode: string | undefined;
      
      // Try to parse as JSON first
      try {
        const data = JSON.parse(payload);
        keyCode = data.keyCode?.toString();
      } catch (e) {
        // Not JSON, treat as plain string (e.g., "Return", "Enter", "C", "1")
        if (typeof payload === 'string') {
          keyCode = payload;
        } else {
          logger.log('[Unity→Browser] Could not parse keyboard input');
          return;
        }
      }
      
      // Execute the key action if it exists
      if (keyCode && keyActions[keyCode]) {
        keyActions[keyCode]();
      } else {
        logger.log(`[Unity→Browser] No handler for key: ${keyCode}`);
      }
    };

    // Client loaded handler
    const handleClientLoadedWorldAndReady = (event: Event) => {
      const unityEvent = event as UnityMessageEvent;
      logger.log('[Unity→Browser] Client loaded world and ready!', unityEvent.detail);
      
      // Set Unity world as ready - this will show all preloaded widgets
      setIsUnityWorldReady(true);
      logger.log('[Unity→Browser] Widgets now visible');
      
      // Start analytics session (use ref to access latest analytics functions)
      analyticsRef.current.startSession();
      // Enter initial room
      analyticsRef.current.enterRoom(currentRoomIdRef.current, 'Intersect Main Room');
      logger.log('[Analytics] Session started and entered initial room:', currentRoomIdRef.current);
    };

    // Player transform handler
    const handlePlayerTransform = (event: Event) => {
      const unityEvent = event as UnityMessageEvent;
      const payload = unityEvent.detail;
      
      try {
        const data = JSON.parse(payload);
        const currentY = data.position?.y;
        
        // logger.log('[Unity→Browser] Player transform update:', {
        //   position: data.position,
        //   rotation: data.rotation,
        //   currentY,
        //   lastStableY: lastYPositionRef.current
        // });
        
        // Initialize lastYPosition on first update
        if (currentY !== undefined && lastYPositionRef.current === null) {
          lastYPositionRef.current = currentY;
          // logger.log('[Unity→Browser] Initialized stable Y position:', currentY);
          return;
        }
        
        // Fall detection - check against the last "stable" Y position
        if (currentY !== undefined && lastYPositionRef.current !== null) {
          const yDifference = currentY - lastYPositionRef.current;
          
          // logger.log('[Unity→Browser] Y difference from stable position:', yDifference);
          
          // If player fell more than 30 units from stable position
          if (yDifference < -30 && !hasFallPopupShownRef.current && !showRespawnPopup && !showRespawnBanner) {
            // logger.log('[Unity→Browser] ⚠️ FALL DETECTED! Y difference:', yDifference);
            setShowRespawnPopup(true);
            hasFallPopupShownRef.current = true; // Prevent popup spam
          }
          // If player is rising or maintaining height, update stable position
          else if (yDifference >= -5) {
            // Only update stable position if player is not falling significantly
            lastYPositionRef.current = currentY;
            // logger.log('[Unity→Browser] Updated stable Y position:', currentY);
          }
          // If falling but less than 30 units, keep the stable position reference
        }
      } catch (e) {
        console.error('[Unity→Browser] Error parsing PlayerTransform data:', e);
      }
    };

    // Area trigger handler - switches rooms based on areaId mapping
    const handleAreaTriggerEntered = (event: Event) => {
      const unityEvent = event as UnityMessageEvent;
      const payload = unityEvent.detail;
      
      try {
        const data = JSON.parse(payload);
        const { playerId, areaId, entered } = data;
        
        logger.log('[Unity→Browser] Area trigger:', {
          playerId,
          areaId,
          entered: entered ? 'ENTERED' : 'EXITED'
        });
        
        // Map Unity area IDs to room IDs
        const areaToRoomMap: { [key: string]: string } = {
          'breakout-room-1': 'intersect-room-01',
          'breakout-room-2': 'intersect-room-02',
          'breakout-room-3': 'intersect-room-03',
          'breakout-room-4': 'intersect-room-04',
        };
        
        // Check if entered (handle both string 'ENTERED' and boolean true)
        const isEntering = entered === 'ENTERED' || entered === true;
        const isExiting = entered === 'EXITED' || entered === false;
        
        const targetRoomId = areaToRoomMap[areaId];
        
        if (isEntering && targetRoomId) {
          // ENTERED: Show "E to interact" prompt
          logger.log(`[Unity→Browser] Player ${playerId} entered area: ${areaId}`);
          
          const actualCurrentRoom = currentRoomIdRef.current;
          
          // Only show prompt if not already in this room
          if (targetRoomId !== actualCurrentRoom) {
            const cachedRoom = roomCacheRef.current[targetRoomId];
            
            if (cachedRoom) {
              logger.log(`[Unity→Browser] Showing area trigger prompt for ${targetRoomId}`);
              setPendingAreaTriggerRoom(cachedRoom);
              setShowAreaTriggerPrompt(true);
            } else {
              logger.warn(`[Unity→Browser] Room ${targetRoomId} not found in cache`);
            }
          } else {
            logger.log(`[Unity→Browser] Already in room ${actualCurrentRoom}, no prompt needed`);
          }
        } else if (isExiting && targetRoomId) {
          // EXITED: Hide prompt and clear pending room
          logger.log(`[Unity→Browser] Player ${playerId} exited area: ${areaId}`);
          
          setShowAreaTriggerPrompt(false);
          setPendingAreaTriggerRoom(null);
        } else if (!targetRoomId) {
          logger.log(`[Unity→Browser] Unknown area ID: ${areaId}, no action taken`);
        }
        
      } catch (e) {
        console.error('[Unity→Browser] Error parsing AreaTriggerEntered data:', e);
      }
    };

    // Custom interactable handler - teleports player to specific rooms
    const handleCustomInteractable = async (event: Event) => {
      const unityEvent = event as UnityMessageEvent;
      const payload = unityEvent.detail;
      
      // Check cooldown to prevent spam
      const now = Date.now();
      const timeSinceLastInteraction = now - lastInteractableTimeRef.current;
      
      if (timeSinceLastInteraction < INTERACTABLE_COOLDOWN_MS) {
        logger.log(`[Unity→Browser] Interactable cooldown active (${Math.ceil((INTERACTABLE_COOLDOWN_MS - timeSinceLastInteraction) / 1000)}s remaining)`);
        return;
      }
      
      try {
        const data = JSON.parse(payload);
        const { id } = data;
        
        logger.log('[Unity→Browser] Custom interactable triggered:', { id });
        
        // Check if it's a call room interaction (opens Google Meet in new tab)
        const callRoomLinks: { [key: string]: string } = {
          'call-room-enter-1': 'https://meet.google.com/wdg-nbqh-vxu',
          'call-room-enter-2': 'https://meet.google.com/kgt-pgui-ide',
          'call-room-enter-3': 'https://meet.google.com/ygj-pdpz-tan',
        };
        
        if (callRoomLinks[id]) {
          // Update last interaction time to start cooldown
          lastInteractableTimeRef.current = now;
          
          const meetUrl = callRoomLinks[id];
          logger.log(`[Unity→Browser] Call room ${id} triggered, opening: ${meetUrl}`);
          window.open(meetUrl, '_blank', 'noopener,noreferrer');
          return;
        }
        
        // Check if it's an NPC interaction (npc-1 through npc-6, etc.)
        if (id.startsWith('npc-')) {
          if (BREAKOUT_GUIDELINES_UNITY_NPC_IDS.has(id)) {
            if (now - breakoutGuidelinesOpenedFromWebAtRef.current < BREAKOUT_GUIDELINES_UNITY_NPC_DEDUPE_MS) {
              logger.log(
                `[Unity→Browser] NPC ${id} skipped (breakout guidelines already shown from room enter)`
              );
              return;
            }
            lastInteractableTimeRef.current = now;
            breakoutGuidelinesOpenedFromWebAtRef.current = now;
            logger.log(`[Unity→Browser] NPC ${id} triggered, showing breakout guidelines popup`);
            setShowBreakoutGuidelines(true);
            return;
          }

          // Update last interaction time to start cooldown
          lastInteractableTimeRef.current = now;

          logger.log(`[Unity→Browser] NPC interactable ${id} triggered, showing dialog`);

          setCurrentNPCId(id);
          setCurrentNPCMessageIndex(0);
          setShowNPCDialog(true);
          return;
        }
        
        // Check if it's a terminal interaction (opens room selector)
        if (id === 'terminal' || id.toLowerCase() === 'terminal') {
          // Update last interaction time to start cooldown
          lastInteractableTimeRef.current = now;
          
          logger.log(`[Unity→Browser] Terminal interactable triggered, opening Room Selector`);
          
          if (roomSelectorRef.current) {
            roomSelectorRef.current.open();
            logger.log('[Unity→Browser] ✅ Room Selector open() called from CustomInteractable');
          } else {
            logger.warn('[Unity→Browser] ❌ roomSelectorRef.current is null! RoomSelector may be hidden (presenter mode?)');
          }
          return;
        }
        
        // Map interactable IDs to room IDs
        const interactableToRoomMap: { [key: string]: string } = {
          'room-enter-1': 'intersect-room-01',
          'room-enter-2': 'intersect-room-02',
          'room-enter-3': 'intersect-room-03',
          'room-enter-4': 'intersect-room-04',
        };
        
        const targetRoomId = interactableToRoomMap[id];
        
        if (targetRoomId) {
          // Update last interaction time to start cooldown
          lastInteractableTimeRef.current = now;
          
          // Check if rooms are available in cache
          const cacheSize = Object.keys(roomCacheRef.current).length;
          const actualCurrentRoom = currentRoomIdRef.current; // Use ref for immediate value
          
          logger.log(`[Unity→Browser] Checking room cache status:`, {
            isLoadingRooms,
            cacheSize,
            cachedRoomIds: Object.keys(roomCacheRef.current),
            currentRoomId_STATE: currentRoomId,
            currentRoomId_REF: actualCurrentRoom,
            targetRoomId
          });

          // Check actual cache size instead of just loading state (more reliable)
          if (isLoadingRooms && cacheSize === 0) {
            logger.log(`[Unity→Browser] Rooms still loading and cache is empty, please wait...`);
            window.alert('Loading rooms... Please try again in a moment.');
            return;
          }
          
          // If cache has rooms but isLoadingRooms is stuck, log warning but proceed
          if (isLoadingRooms && cacheSize > 0) {
            logger.warn(`[Unity→Browser] ⚠️ isLoadingRooms stuck at true, but cache has ${cacheSize} rooms. Proceeding anyway...`);
          }
          
          // TOGGLE BEHAVIOR: If already in target room, return to main instead
          let finalRoomId = targetRoomId;
          if (actualCurrentRoom === targetRoomId) {
            logger.log(`[Unity→Browser] 🔄 Already in ${targetRoomId}, toggling back to main room`);
            finalRoomId = 'intersect-main';
          } else {
            logger.log(`[Unity→Browser] Interactable ${id} triggered, teleporting to room: ${targetRoomId}`);
          }
          
          // Try to get room from cache first
          const cachedRoom = roomCacheRef.current[finalRoomId];
          
          if (cachedRoom) {
            logger.log(`[Unity→Browser] Using cached room data for ${finalRoomId}:`, cachedRoom);
            handleRoomChangeRequest(cachedRoom);
          } else {
            // Fallback to API call if not in cache
            logger.warn(`[Unity→Browser] Room ${finalRoomId} not in cache, fetching from API...`);
            
            try {
              const response = await API.getRoom(accessToken, {
                eventId: eventId,
                roomId: finalRoomId
              });
              
              if (response.status !== 200) {
                throw new Error(`Failed to fetch room: ${response.status}`);
              }
              
              const roomData = await response.json();
              logger.log(`[Unity→Browser] Fetched room data for ${finalRoomId}:`, roomData);
              
              // Cache it for next time
              roomCacheRef.current[finalRoomId] = roomData;
              
              // Switch room and teleport player (gated for private bookings)
              handleRoomChangeRequest(roomData);
            } catch (error) {
              console.error(`[Unity→Browser] Error fetching room ${finalRoomId}:`, error);
              window.alert(`Unable to teleport to room. Please try again or contact support.`);
            }
          }
        } else {
          logger.warn(`[Unity→Browser] Unknown interactable ID: ${id}`);
        }
        
      } catch (e) {
        console.error('[Unity→Browser] Error parsing CustomInteractable data:', e);
      }
    };

    // Terminal event handler - opens room selector
    const handleTerminalEvent = (event: Event) => {
      const unityEvent = event as UnityMessageEvent;
      const payload = unityEvent.detail;
      
      logger.log('[Unity→Browser] Terminal event received:', {
        payload,
        roomSelectorRef: roomSelectorRef.current,
        refIsNull: roomSelectorRef.current === null
      });
      
      const openRoomSelector = () => {
        if (roomSelectorRef.current) {
          roomSelectorRef.current.open();
          logger.log('[Unity→Browser] ✅ Room Selector open() called successfully');
        } else {
          logger.warn('[Unity→Browser] ❌ roomSelectorRef.current is null! This usually means:');
          logger.warn('  - User is in Presenter Mode (isAtPodium && isLowResMode), which hides the RoomSelector');
          logger.warn('  - OR the Unity world is not yet ready');
          logger.warn('  - OR isUIVisible is false (Ctrl+H to toggle)');
        }
      };
      
      try {
        const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
        
        // Check for id === 'terminal' (case-insensitive)
        const id = data?.id?.toLowerCase?.() || '';
        if (id === 'terminal') {
          logger.log('[Unity→Browser] Terminal id matched, attempting to open Room Selector');
          openRoomSelector();
        } else {
          logger.log('[Unity→Browser] Terminal event id mismatch, expected "terminal", got:', data?.id);
        }
      } catch (e) {
        // Maybe payload itself is just the id string
        if (payload === 'terminal' || payload?.toLowerCase?.() === 'terminal') {
          logger.log('[Unity→Browser] Terminal event (plain string), attempting to open Room Selector');
          openRoomSelector();
        } else {
          console.error('[Unity→Browser] Error parsing Terminal event data:', e, 'payload:', payload);
        }
      }
    };

    const handlePlayerClicked = (event: Event) => {
      const unityEvent = event as UnityMessageEvent;
      const payload = unityEvent.detail;
      try {
        const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
        const userId = typeof data?.userId === 'string' ? data.userId.trim() : '';
        if (!userId) {
          return;
        }
        const token = accessTokenRef.current;
        if (!token) {
          logger.warn('[Unity→Browser] PlayerClicked skipped: no accessToken');
          return;
        }
        const displayName = typeof data?.displayName === 'string' ? data.displayName : '';
        logger.log('[Unity→Browser] PlayerClicked', { userId, displayName });
        setPlayerClickedPanel({ userId, displayName });
      } catch (e) {
        console.error('[Unity→Browser] Error parsing PlayerClicked data:', e);
      }
    };

    // Add event listeners
    window.addEventListener('unityMessage', handleUnityMessage);
    window.addEventListener('KeyboardInput', handleKeyboardInput);
    window.addEventListener('ClientLoadedWorldAndReady', handleClientLoadedWorldAndReady);
    window.addEventListener('PlayerTransform', handlePlayerTransform);
    window.addEventListener('CustomInteractable', handleCustomInteractable);
    window.addEventListener('AreaTriggerEntered', handleAreaTriggerEntered);
    window.addEventListener('Terminal', handleTerminalEvent);
    window.addEventListener('PlayerClicked', handlePlayerClicked);
    logger.log(
      '[Unity→Browser] Listeners registered for Unity events (unityMessage, KeyboardInput, ClientLoadedWorldAndReady, PlayerTransform, CustomInteractable, AreaTriggerEntered, Terminal, PlayerClicked)'
    );

    // Cleanup on unmount
    return () => {
      window.removeEventListener('unityMessage', handleUnityMessage);
      window.removeEventListener('KeyboardInput', handleKeyboardInput);
      window.removeEventListener('ClientLoadedWorldAndReady', handleClientLoadedWorldAndReady);
      window.removeEventListener('PlayerTransform', handlePlayerTransform);
      window.removeEventListener('CustomInteractable', handleCustomInteractable);
      window.removeEventListener('AreaTriggerEntered', handleAreaTriggerEntered);
      window.removeEventListener('Terminal', handleTerminalEvent);
      window.removeEventListener('PlayerClicked', handlePlayerClicked);
      logger.log('[Unity→Browser] Listeners removed');
    };
  }, []);

  // Image cycling effect for loading screen
  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      // Fade out
      setImageOpacity(0);
      
      // After fade out completes, change image and fade in
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % loadingImages.length
        );
        setImageOpacity(1);
      }, 500); // Half second for fade out
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    const loadUnityScript = () => {
      const script = document.createElement('script');
      script.src = `${show.upc_url}/Build/FutureFestXR.loader.js`;
      script.onload = async () => {
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/agora-extension-virtual-background.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/virtualbackground.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/databuilder.js`);
        await loadScript('https://ff-desktop-clients.s3.us-east-1.amazonaws.com/UnityGame/Test/clientmanager.js');
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/wglwrapper.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/audioeffects.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/eventmanager.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/engineglobals.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/watermark.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/customvideo.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/agorachannel.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/multichannel.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/audiosystem.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/testing.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/agorautils.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/audiomixing.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/agorartcenginev2.js`);
        await loadScript(`${show.upc_url}/AgoraRTC_N-4.21.0.js`);
        await loadScript(`${show.upc_url}/AgoraWebSDK/libs/spatial-audio-main.js`, 'module');
        await loadScript(`${show.upc_url}/TemplateData/hls.min.js`);
        window.__ffSpatialIvsAudioEnabled = show?.spatial_ivs_audio !== false;
        // IVS face pins over Unity canvas — disabled (opt-in via window.__ffIvsFacePinsEnabled === true in IVSRealtimePanel).
        (window as any).__ffIvsFacePinsEnabled = false;
        window.__ffIvsStageMuted = true;
        // IVS Broadcast Client is loaded via npm import and exposed on window.IVSBroadcastClient
        await loadScript(`${show.upc_url}/TemplateData/ivs.js`);
        await loadScript(`${show.upc_url}/TemplateData/ivs-audio-patch.js`);

        window
          .createUnityInstance(
            canvasRef.current,
            {
              arguments: [
                '-playerType',
                'player',
                '-api_url',
                process.env.REACT_APP_API_URL,
                '-env',
                'dev',
                '-jwtMethod',
                'commandline',
                '-conversationalDistance',
                show.conversational_distance,
                '-audibleDistance',
                show.audible_distance,
                '-worldMapContentId',
                show.world_map_content_id,
                '-worldMapContentUrl',
                show.upc_world_map_content_url,
                '-contentLoadUrl',
                show.content_load_url,
                '-defaultZoomEval',
                show.default_zoom_eval,
                '-zoomMax',
                show.zoom_max,
                '-streamType',
                show.stream_type,
                '-spatialIvsAudio',
                show.spatial_ivs_audio === false ? 'false' : 'true',
                '-ivsFacePins',
                'false',
                '-inventoryUIDisable',
                show.inventory_ui_disable,
                '-actionbarUIDisable',
                show.actionbar_ui_disable,
                '-chatUIDisable',
                show.chat_ui_disable,
                '-jwt',
                accessToken
              ],
              dataUrl: `${show.upc_url}/Build/FutureFestXR.data`,
              frameworkUrl: `${show.upc_url}/Build/FutureFestXR.framework.js`,
              codeUrl: `${show.upc_url}/Build/FutureFestXR.wasm`,
              streamingAssetsUrl: `${show.upc_url}/StreamingAssets`,
              companyName: 'FutureFest',
              productName: 'FutureFestXR',
              productVersion: '2.0.0'
            },
            (progress: number) => {
              setLoadingProgress(progress * 100);
            }
          )
          .then((unityInstance: any) => {
            unityInstanceRef.current = unityInstance;
            window.unityInstance = unityInstance;
            
            // Expose bridge function globally
            window.sendMessageToUnity = (gameObjectName: string, methodName: string, parameter?: string | number) => {
              logger.log(`Sending to Unity: GameObject="${gameObjectName}", Method="${methodName}", Param="${parameter}"`);
              try {
                if (parameter !== undefined) {
                  unityInstance.SendMessage(gameObjectName, methodName, parameter);
                } else {
                  unityInstance.SendMessage(gameObjectName, methodName);
                }
                logger.log('Message sent successfully to Unity');
              } catch (error) {
                console.error('Error sending message to Unity:', error);
              }
            };
            
            setIsLoading(false);
          })
          .catch((message: any) => {
            alert(message);
          });
      };
      document.body.appendChild(script);
    };

    loadUnityScript();

    // Cleanup function to destroy the Unity instance when component unmounts
    return () => {
      if (unityInstanceRef.current) {
        unityInstanceRef.current.Quit();
        unityInstanceRef.current = null;
        window.unityInstance = null;
      }
    };
  }, [accessToken, show]);

  // Fetch default room data on mount
  // Fetch and cache all rooms at startup for fast teleportation
  useEffect(() => {
    let isMounted = true;
    
    const fetchAllRooms = async () => {
      logger.log('[Room Cache] 🚀 useEffect triggered! Starting room cache fetch...', {
        hasAccessToken: !!accessToken,
        eventId,
        currentLoadingState: isLoadingRooms,
        timestamp: new Date().toISOString()
      });

      if (!accessToken) {
        logger.log('[Room Cache] Skipping - no accessToken');
        if (isMounted) {
          setIsLoadingRooms(false);
        }
        return;
      }

      try {
        logger.log('[Room Cache] 📡 Fetching all rooms for event:', eventId);
        const response = await API.getRooms(accessToken, eventId);
        logger.log('[Room Cache] 📥 API response status:', response.status);
        
        if (response.status !== 200) {
          console.error('[Room Cache] ❌ Failed to fetch rooms:', response.status);
          const errorText = await response.text().catch(() => 'Unable to read error');
          console.error('[Room Cache] Error response:', errorText);
          if (isMounted) {
            setIsLoadingRooms(false);
          }
          return;
        }

        const rooms = await response.json();
        logger.log('[Room Cache] 📦 Fetched rooms:', rooms);

        // Cache rooms by room_id for fast lookup
        const cache: { [key: string]: any } = {};
        if (Array.isArray(rooms)) {
          rooms.forEach((room: any) => {
            if (room.room_id) {
              cache[room.room_id] = room;
              logger.log('[Room Cache] 💾 Caching room:', room.room_id, room.name || room.room_name);
            }
          });
          logger.log('[Room Cache] ✅ Successfully cached', Object.keys(cache).length, 'rooms:', Object.keys(cache));
        } else {
          logger.warn('[Room Cache] ⚠️ Rooms response is not an array:', typeof rooms);
        }

        roomCacheRef.current = cache;
        logger.log('[Room Cache] 🔄 About to set isLoadingRooms to false...', { isMounted });
        
        if (isMounted) {
          setIsLoadingRooms(false);
          logger.log('[Room Cache] ✅ Cache ready! isLoadingRooms set to false');
        } else {
          logger.warn('[Room Cache] ⚠️ Component unmounted, skipping state update');
        }
      } catch (error) {
        console.error('[Room Cache] ❌ Error fetching rooms:', error);
        if (isMounted) {
          setIsLoadingRooms(false);
        }
      }
    };

    fetchAllRooms();
    
    return () => {
      logger.log('[Room Cache] 🧹 Cleanup - component unmounting');
      isMounted = false;
    };
  }, [accessToken, eventId]);

  useEffect(() => {
    logger.log('[Unity] ==== Room fetch useEffect TRIGGERED ====', { 
      hasAccessToken: !!accessToken, 
      eventId, 
      currentRoomId 
    });
    
    const fetchDefaultRoom = async () => {
      if (!accessToken) {
        logger.log('[Unity] Skipping room fetch - no accessToken');
        return;
      }
      
      try {
        logger.log('[Unity] Fetching room data for:', currentRoomId);
        const response = await API.getRoom(accessToken, {
          eventId: eventId,
          roomId: currentRoomId
        });
        logger.log('[Unity] Room fetch response status:', response.status);
        
        if (response.status !== 200) {
          console.error('[Unity] Room fetch failed with status:', response.status);
          const errorText = await response.text();
          console.error('[Unity] Error response:', errorText);
          return;
        }
        
        const data = await response.json();
        logger.log('[Unity] Room data received:', data);
        logger.log('[Unity] Room data type:', typeof data);
        logger.log('[Unity] Room data keys:', data ? Object.keys(data) : 'null');
        
        if (data) {
          const chatArn = data.chat_room_arn || data.chatRoomArn || data.chat_arn;
          const stageArn = data.stage_arn || data.stageArn;
          const roomName = data.name || data.room_name || data.roomName;
          const playbackUrl = data.stage_playback_url || data.stagePlaybackUrl || '';
          const podiumPos = data.podium_position || data.podiumPosition || '';
          
          logger.log('[Unity] Extracted values:', { chatArn, stageArn, roomName, playbackUrl, podiumPos });
          
          setChatRoomArn(chatArn);
          setStageArnFromRoom(stageArn);
          setStagePlaybackUrl(playbackUrl);
          setCurrentRoomName(roomName);
          setPodiumPosition(podiumPos);
          
          logger.log('[Unity] Set room ARNs:', {
            chat: chatArn,
            stage: stageArn,
            name: roomName,
            playbackUrl,
            podiumPosition: podiumPos
          });
        } else {
          console.error('[Unity] Room data is null/undefined');
        }
      } catch (error) {
        console.error('[Unity] Error fetching default room:', error);
      }
    };

    fetchDefaultRoom();
  }, [accessToken, eventId, currentRoomId]);

  // Fetch participant token for current room
  useEffect(() => {
    logger.log('[Unity] ==== Participant token useEffect TRIGGERED ====', {
      hasAccessToken: !!accessToken,
      eventId,
      currentRoomId
    });
    
    const fetchParticipantToken = async () => {
      if (!accessToken || !currentRoomId || !eventId) {
        logger.log('[Unity] Skipping participant token fetch:', { accessToken: !!accessToken, currentRoomId, eventId });
        return;
      }
      
      try {
        logger.log('[Unity] Fetching participant token for room:', currentRoomId);
        logger.log('[Unity] Participant token request params:', { eventId, roomId: currentRoomId });
        
        const response = await API.getIVSParticipantTokenForRoom(accessToken, {
          eventId: eventId,
          roomId: currentRoomId
        });
        
        logger.log('[Unity] Participant token fetch completed, status:', response.status);
        
        if (response.status !== 200) {
          console.error('[Unity] Failed to fetch participant token:', response.status);
          const errorText = await response.text();
          console.error('[Unity] Error response:', errorText);
          return;
        }
        
        const data = await response.json();
        logger.log('[Unity] Participant token response data:', data);
        logger.log('[Unity] Participant token response keys:', data ? Object.keys(data) : 'null');
        
        if (data.participantToken) {
          // Extract token, userId, intersectStaff, and stagePlaybackUrl from response
          const token = data.participantToken.token;
          const userId = data.participantToken.userId;
          const intersectStaff = data.participantToken.intersectStaff || false;
          const playbackUrl = data.participantToken.stagePlaybackUrl || '';
          
          logger.log('[Unity] Successfully fetched participant token for room:', currentRoomId);
          logger.log('[Unity] Token length:', token?.length || 'undefined');
          logger.log('[Unity] Extracted userId:', userId);
          logger.log('[Unity] Is Intersect Staff:', intersectStaff);
          logger.log('[Unity] Stage playback URL:', playbackUrl);
          
          // CRITICAL: Set userId FIRST, then token
          // This ensures userId is available when IVSRealtimePanel mounts
          setUserIdFromRoom(userId);
          setIsIntersectStaffFromRoom(intersectStaff);
          setStagePlaybackUrl(playbackUrl);
          setParticipantTokenFromRoom(token);
          
          // Clear room transitioning state now that we have the new token
          setIsRoomTransitioning(false);
          logger.log('[Unity] Room transition complete');
        } else {
          console.error('[Unity] Missing participantToken in response:', data);
          // Clear transitioning state even on error
          setIsRoomTransitioning(false);
        }
      } catch (error) {
        console.error('[Unity] Error fetching participant token:', error);
        console.error('[Unity] Error details:', error instanceof Error ? error.message : String(error));
        // Clear transitioning state even on error
        setIsRoomTransitioning(false);
      } finally {
        logger.log('[Unity] Participant token fetch attempt finished');
      }
    };

    logger.log('[Unity] Calling fetchParticipantToken...');
    fetchParticipantToken();
  }, [accessToken, eventId, currentRoomId]);

  // Resolve display name for hand-raise signals (outgoing attributes + toast labels for others)
  useEffect(() => {
    if (!accessToken || !userIdFromRoom) {
      setHandRaiseDisplayName(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await API.getIntersectUserProfile(accessToken, { userId: userIdFromRoom });
        if (cancelled || !res.ok) return;
        const data = await res.json();
        const name = typeof data?.username === 'string' ? data.username.trim() : '';
        if (name) setHandRaiseDisplayName(name);
        else setHandRaiseDisplayName(undefined);
      } catch {
        if (!cancelled) setHandRaiseDisplayName(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, userIdFromRoom]);

  // Auto-grant screenshare if user has active booking for current room
  // Also revoke if user leaves a room where they had auto-granted permission
  useEffect(() => {
    const checkBookingScreenshare = async () => {
      if (!currentRoomId || !userIdFromRoom || !accessToken) return;

      try {
        const response = await API.getBookings(accessToken, eventId);
        if (response.status !== 200) return;

        const data = await response.json();
        const bookings = data.bookings || [];
        const now = new Date();

        // Find active booking for current room owned by current user
        const activeBooking = bookings.find((booking: any) => {
          if (booking.room_id !== currentRoomId) return false;
          if (booking.booked_by_user_id !== userIdFromRoom) return false;
          if (booking.status === 'cancelled') return false;

          const startTime = new Date(booking.start_time);
          const endTime = new Date(booking.end_time);
          return now >= startTime && now <= endTime;
        });

        if (activeBooking) {
          // User has active booking for this room - grant permission
          logger.log('[Unity] Auto-granting screenshare for active booking:', activeBooking.id, 'in room:', currentRoomId);
          screenShareAutoGrantedForRoom.current = currentRoomId;
          setHasScreenSharePermission(true);
        } else {
          // No active booking for this room
          // If permission was auto-granted for a different room, revoke it
          if (screenShareAutoGrantedForRoom.current && screenShareAutoGrantedForRoom.current !== currentRoomId) {
            logger.log('[Unity] Revoking auto-granted screenshare - left booked room:', screenShareAutoGrantedForRoom.current, 'now in:', currentRoomId);
            screenShareAutoGrantedForRoom.current = null;
            setHasScreenSharePermission(false);
          }
        }
      } catch (error) {
        console.error('Error checking booking screenshare:', error);
      }
    };

    checkBookingScreenshare();
  }, [currentRoomId, userIdFromRoom, accessToken, eventId]);

  // Debug: Track IVS Panel render conditions
  useEffect(() => {
    logger.log('[Unity] IVS Panel state update:', {
      stageArnFromRoom: stageArnFromRoom || 'NOT SET',
      participantTokenFromRoom: participantTokenFromRoom ? 'SET (length: ' + participantTokenFromRoom.length + ')' : 'NOT SET',
      userIdFromRoom: userIdFromRoom || 'NOT SET',
      shouldRenderPanel: !!(stageArnFromRoom && participantTokenFromRoom)
    });
  }, [stageArnFromRoom, participantTokenFromRoom, userIdFromRoom]);

  // Check intersect staff status from chat
  useEffect(() => {
    const checkIntersectStaff = () => {
      if (chatRef.current && chatRef.current.isIntersectStaff) {
        const staffStatus = chatRef.current.isIntersectStaff();
        setIsIntersectStaff(staffStatus);
      }
    };

    // Check initially after a short delay to let chat initialize
    const initialTimeout = setTimeout(checkIntersectStaff, 2000);

    // Check periodically every 5 seconds
    const interval = setInterval(checkIntersectStaff, 5000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  // Show onboarding tour when Unity world is ready and avatar selection is complete
  useEffect(() => {
    if (isUnityWorldReady && !showAvatarSelection && !hasCompletedOnboarding()) {
      // Small delay to let the UI settle before showing tour
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isUnityWorldReady, showAvatarSelection]);

  // Handler to replay the onboarding tour
  const handleReplayTour = () => {
    resetOnboarding();
    setShowOnboarding(true);
  };

  // Handlers for onboarding tour completion
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
  };

  return (
    <div className="relative w-full h-full" onClick={handleCanvasClick}>
      <div
        id="unity-container"
        className="unity-desktop flex flex-col items-center justify-center relative h-[80%] w-full mx-auto"
      >
        <Helmet>{/* Helmet content remains unchanged */}</Helmet>
        <div 
          ref={gameContainerRef}
          className={`relative ${isLowResMode ? '' : 'w-full h-full'}`}
          style={{ 
            backgroundColor: '#231f20',
            // When in low-res mode, minimize this container since canvas is position:fixed
            ...(isLowResMode && { width: 0, height: 0, overflow: 'hidden' })
          }}
          onClick={handleCanvasClick}
          onContextMenu={handleCanvasRightClick}
        >
          {/* Unity Canvas - Always rendered */}
        <canvas 
          ref={canvasRef} 
          id="unity-canvas" 
            className="bg-[#231f20] cursor-pointer"
            style={isLowResMode ? {
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '240px',
              height: '135px',
              zIndex: 100,
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            } : {
              width: '100%',
              height: '100%'
            }}
            onClick={handleCanvasClick}
            onContextMenu={handleCanvasRightClick}
        ></canvas>
        <div
          id="unity-game-face-pins-root"
          className="pointer-events-none absolute inset-0 z-[4] overflow-hidden"
          aria-hidden
        />
          {/* Stage View Overlay - Covers canvas when active */}
          {isStageViewMode && (
            <div 
              className="absolute inset-0"
              style={{ zIndex: 5 }}
            >
              <StagePlayer
                playbackUrl={stagePlaybackUrl}
                isFullscreen={true}
                onClose={() => handleStageViewToggle(false)}
                isMuted={isStageMuted || isLocalUserScreenSharing}
                onToggleMute={() => setIsStageMuted(!isStageMuted)}
                className="w-full h-full"
              />
          </div>
        )}
        </div>
        
        {/* UI elements outside gameContainerRef so they're not affected by canvas position changes */}
        {!isFullscreen && isUIVisible && !isLowResMode && (
          <button
            id="unity-fullscreen-button"
            data-tour-id="tour-fullscreen-button"
            onClick={(e) => {
              e.stopPropagation();
              handleFullscreenClick();
            }}
            className="unity-custom-cursor p-2 bg-gray-800 bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200"
            style={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 'var(--unity-fullscreen-button-bottom)',
              zIndex: 10000,
            }}
          >
            <FaExpand size={20} />
          </button>
        )}

        {/* Private room gate modal: enter code to join locked room */}
        {privateRoomGateRoom && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
            onClick={handlePrivateRoomGateCancel}
          >
            <div
              className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-white text-xl font-semibold mb-2">Room is private</h3>
              <p className="text-gray-400 text-sm mb-4">
                Enter the code to join: {privateRoomGateTitle}
              </p>
              <input
                type="text"
                value={privateRoomGateCodeInput}
                onChange={(e) => {
                  setPrivateRoomGateCodeInput(e.target.value.toUpperCase());
                  setPrivateRoomGateError(null);
                }}
                placeholder="e.g. ABC123"
                className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#444] rounded-lg text-white placeholder-gray-500 font-mono focus:border-green-500 focus:outline-none mb-3"
                maxLength={12}
                disabled={privateRoomGateSubmitting}
              />
              {privateRoomGateError && (
                <p className="text-red-400 text-sm mb-3">{privateRoomGateError}</p>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={handlePrivateRoomGateCancel}
                  className="px-4 py-2 bg-[#2a2a2a] text-white rounded-lg hover:bg-[#333] transition-all"
                  disabled={privateRoomGateSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePrivateRoomGateSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                  disabled={!privateRoomGateCodeInput.trim() || privateRoomGateSubmitting}
                >
                  {privateRoomGateSubmitting ? 'Checking...' : 'Join'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Controls and Video Chat Widgets */}
        {!isLoading && isUIVisible && (
          <>
          <div
            className="unity-widgets-container unity-dock-tray unity-custom-cursor pointer-events-auto fixed z-10 transition-all duration-300 ease-in-out"
            style={{
              visibility: isUnityWorldReady ? 'visible' : 'hidden',
              zIndex: 10000,
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {isAtPodium && (
              <LeavePodium onLeavePodium={handleLeavePodium} />
            )}
            {/* Show leave breakout room button when in a breakout room */}
            {!isPresenterMode && currentRoomId !== 'intersect-main' && (
              <LeaveBreakoutRoom 
                onLeaveRoom={() => {
                  const mainRoom = roomCacheRef.current['intersect-main'];
                  if (mainRoom) {
                    logger.log('[Unity] Returning to main room from breakout room');
                    handleRoomChange(mainRoom, false); // Teleport to main room
                  }
                }} 
              />
            )}
            {/* Clap + hand raise: breakout rooms only */}
            {!isPresenterMode && currentRoomId !== 'intersect-main' && (
              <BreakoutClapDock
                disabled={!chatRoomArn || !accessToken}
                onClap={async () => {
                  const chat = chatRef.current;
                  if (!chat?.sendBreakoutClap) throw new Error('Chat unavailable');
                  await chat.sendBreakoutClap();
                }}
              />
            )}
            {!isPresenterMode && currentRoomId !== 'intersect-main' && (
              <SocialInteraction
                dataTourId="tour-highfive-button"
                disabled={!chatRoomArn || !accessToken}
                syncRaisedFromQueue={isLocalHandRaisedInQueue}
                onHandRaiseChange={async (raised) => {
                  const chat = chatRef.current;
                  if (!chat?.sendHandRaiseState) throw new Error('Chat unavailable');
                  await chat.sendHandRaiseState(raised);
                }}
              />
            )}
            <Agenda
              accessToken={accessToken || ''}
              eventId={eventId}
              currentRoomId={currentRoomId}
              onRoomChange={(room) => handleRoomChangeRequest(room)}
              cachedRooms={Object.values(roomCacheRef.current)}
              isStaff={isIntersectStaff}
              currentUserId={userIdFromRoom || ivsUserId}
            />
            {/* Hide room selector and controls in presenter mode */}
            {!isPresenterMode && (
              <div data-tour-id="tour-rooms-button">
                <RoomSelector
                  ref={roomSelectorRef}
                  dockMode
                  accessToken={accessToken || ''}
                  eventId={eventId}
                  currentRoomId={currentRoomId}
                  onRoomChange={(room) => handleRoomChangeRequest(room)}
                  cachedRooms={Object.values(roomCacheRef.current)}
                  isLoadingRooms={isLoadingRooms}
                  isStaff={isIntersectStaff}
                  onCodeUnlocked={(code) => setUnlockedCodes((prev) => (prev.includes(code) ? prev : [...prev, code]))}
                  onRoomChangeDirect={handleRoomChange}
                />
              </div>
            )}
            {/* ModTools - only show when room is fully loaded (keep visible for staff even in presenter mode) */}
            {stageArnFromRoom && participantTokenFromRoom && isIntersectStaff && (
              <ModTools
                dockMode
                accessToken={accessToken || ''}
                eventId={eventId}
                currentRoomId={currentRoomId}
                chatRef={chatRef}
                podiumPosition={podiumPosition}
                chatRoomAvailable={Boolean(chatRoomArn)}
                currentUserId={userIdFromRoom || ivsUserId}
                onPodiumTeleport={() => setIsAtPodium(true)}
              />
            )}
            
            {/* Help widget - hide in presenter mode */}
            {!isPresenterMode && accessToken && eventId && (
              <RoomUsersWidget
                dockMode
                eventId={eventId}
                roomId={currentRoomId}
                accessToken={accessToken}
                currentUserId={userIdFromRoom || ivsUserId}
                raisedHands={handRaiseNotificationItems}
                isIntersectStaff={isIntersectStaff}
                onModeratorUnraiseHand={
                  isIntersectStaff && accessToken && chatRoomArn
                    ? handleModeratorUnraiseHand
                    : undefined
                }
              />
            )}
            {!isPresenterMode && (
              <HelpWidget
                dockMode
                dataTourId="tour-help-button"
                onReplayTour={handleReplayTour}
                onOpenKeyboardControls={handleControlsClick}
                graphicsQuality={graphicsQuality}
                autoDetectedQuality={autoDetectedQuality}
                onGraphicsQualityChange={setGraphicsQuality}
              />
            )}
            
            {/* Video dock: popover with live preview; keep tile visible while popover open (sidebar expanded hides tile otherwise). */}
            {(!isIVSPanelEnabled || isIVSPanelCollapsed || videoDockPopoverOpen) && (
              <div ref={videoDockTileRef}>
                <UnityGameDockApp
                  popoverOwnerId={videoDockPopoverOwnerId}
                  dataTourId="tour-video-dock"
                  ariaLabel={videoDockPopoverOpen ? 'Close video menu' : 'Video — preview and controls'}
                  label="Video"
                  icon={<FaVideo />}
                  active={videoDockPopoverOpen}
                  tileClassName="bg-gradient-to-br from-violet-600 to-fuchsia-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isIVSPanelEnabled) {
                      logger.log('[IVS Panel] Re-enabling panel and rejoining stream');
                      setIsIVSPanelEnabled(true);
                    }
                    setIsIVSPanelCollapsed(true);
                    setScreenShareDockPopoverOpen(false);
                    setVideoDockPopoverOpen((o) => {
                      if (o) {
                        localVideoControlApiRef.current?.closeCameraPreview?.();
                      }
                      return !o;
                    });
                  }}
                >
                  {videoDockPopoverOpen && (
                    <div
                      className="unity-dock-popover unity-dock-popover--enter unity-dock-popover--wide unity-dock-popover--video flex w-[min(96vw,28rem)] max-w-[28rem] flex-col gap-2 border-gray-700 bg-gray-900 p-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Video</div>
                      <div
                        ref={videoCameraPreviewHostRef}
                        className="w-full shrink-0 rounded-lg border border-gray-700/80 bg-black/40"
                      />
                      {!localVideoControlApi ? (
                        <p className="text-xs text-gray-500">Waiting for video session…</p>
                      ) : localVideoControlApi.isFetchingVideoCallToken ? (
                        <p className="flex items-center gap-2 text-xs text-gray-400">
                          <FaSpinner className="animate-spin" size={14} />
                          Checking room video…
                        </p>
                      ) : !localVideoControlApi.isVideoCallConnected ? (
                        <p className="text-xs text-gray-500">Video call not connected yet.</p>
                      ) : null}
                      {localVideoControlApi?.cameraPreviewError ? (
                        <p className="text-xs text-red-300">{localVideoControlApi.cameraPreviewError}</p>
                      ) : null}
                      {localVideoControlApi?.isAtVideoLimit && !localVideoControlApi.localVideoEnabled ? (
                        <p className="text-xs text-amber-400">Room video publisher limit reached.</p>
                      ) : null}
                      {localVideoControlApi?.isVideoCallConnected &&
                      localVideoControlApi.localVideoEnabled &&
                      !localVideoControlApi.isCameraPreviewOpen ? (
                        <>
                          <div className="relative aspect-video max-h-44 overflow-hidden rounded-lg border border-gray-600 bg-black">
                            <DockLocalVideoPreview
                              stream={localVideoControlApi.localStream}
                              enabled={localVideoControlApi.localVideoEnabled}
                            />
                            {localVideoControlApi.localVideoLoading ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                                <FaSpinner className="animate-spin text-white" size={28} />
                              </div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => void localVideoControlApi.toggleLocalVideo()}
                            className="flex w-full items-center justify-center gap-2 rounded-md border border-blue-500 bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-500"
                          >
                            <FaVideo />
                            Turn off camera
                          </button>
                        </>
                      ) : null}
                      {localVideoControlApi &&
                      !localVideoControlApi.localVideoEnabled &&
                      !localVideoControlApi.isCameraPreviewOpen &&
                      !localVideoControlApi.isFetchingVideoCallToken &&
                      !(localVideoControlApi.isVideoCallAvailable && localVideoControlApi.isAtVideoLimit) ? (
                        <button
                          type="button"
                          onClick={() => localVideoControlApi.openCameraPreview()}
                          className="w-full rounded-md border border-gray-600 bg-[#2a2a2a] px-3 py-2 text-sm font-medium text-white hover:bg-[#333]"
                        >
                          Camera preview…
                        </button>
                      ) : null}
                    </div>
                  )}
                </UnityGameDockApp>
              </div>
            )}

            {!isPresenterMode && (hasScreenSharePermission || Boolean(screenShareControlApi?.screenShareEnabled)) && (
              <div ref={screenShareDockTileRef}>
                <UnityGameDockApp
                  popoverOwnerId={screenShareDockPopoverOwnerId}
                  dataTourId="tour-screenshare-dock"
                  ariaLabel={screenShareDockPopoverOpen ? 'Close screen share menu' : 'Screen share'}
                  label="Share"
                  icon={<FaDesktop />}
                  active={screenShareDockPopoverOpen}
                  tileClassName="bg-gradient-to-br from-emerald-700 to-teal-900"
                  onClick={(e) => {
                    e.stopPropagation();
                    setVideoDockPopoverOpen(false);
                    if (!isIVSPanelEnabled) {
                      setIsIVSPanelEnabled(true);
                    }
                    setIsIVSPanelCollapsed(true);
                    setScreenShareDockPopoverOpen((o) => !o);
                  }}
                >
                  {screenShareDockPopoverOpen && (
                    <div
                      className="unity-dock-popover unity-dock-popover--enter unity-dock-popover--wide flex w-[min(92vw,20rem)] max-w-[20rem] flex-col gap-3 border-gray-700 bg-gray-900 p-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Screen sharing</div>
                      {!screenShareControlApi ? (
                        <p className="text-sm text-gray-500">Waiting for video session…</p>
                      ) : (
                        <>
                          {!screenShareControlApi.screenShareEnabled && (
                            <div className="flex gap-2" data-tour-id="tour-screenshare-quality">
                              <select
                                value={screenShareControlApi.screenShareQuality}
                                onChange={(e) =>
                                  screenShareControlApi.setScreenShareQuality(e.target.value as ScreenShareQuality)
                                }
                                className="flex-1 cursor-pointer rounded-md border border-[#444] bg-[#2a2a2a] p-2.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                                disabled={
                                  !screenShareControlApi.isScreenShareStageConnected ||
                                  screenShareControlApi.screenShareLoading
                                }
                              >
                                {(Object.keys(SCREEN_SHARE_QUALITY_PRESETS) as ScreenShareQuality[]).map((key) => (
                                  <option key={key} value={key}>
                                    {SCREEN_SHARE_QUALITY_PRESETS[key].label} (
                                    {SCREEN_SHARE_QUALITY_PRESETS[key].description})
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          <button
                            type="button"
                            className={`flex w-full items-center justify-center gap-2 rounded-md p-2.5 text-base text-white transition-all duration-200 ${
                              screenShareControlApi.screenShareEnabled
                                ? 'border border-green-500 bg-green-500 hover:bg-green-600'
                                : 'border border-[#444] bg-[#2a2a2a] hover:border-[#555] hover:bg-[#3a3a3a]'
                            } ${
                              !screenShareControlApi.isScreenShareStageConnected ||
                              screenShareControlApi.screenShareLoading
                                ? 'cursor-not-allowed opacity-50'
                                : ''
                            }`}
                            onClick={() => void screenShareControlApi.toggleScreenShare()}
                            disabled={
                              !screenShareControlApi.isScreenShareStageConnected ||
                              screenShareControlApi.screenShareLoading
                            }
                            title={
                              !screenShareControlApi.isScreenShareStageConnected
                                ? 'Screen share stage not connected'
                                : screenShareControlApi.screenShareEnabled
                                  ? 'Stop sharing screen'
                                  : `Share your screen (${SCREEN_SHARE_QUALITY_PRESETS[screenShareControlApi.screenShareQuality].description})`
                            }
                            data-tour-id="tour-screenshare-button"
                          >
                            {screenShareControlApi.screenShareLoading ? (
                              <FaSpinner className="animate-spin" />
                            ) : (
                              <FaDesktop />
                            )}
                            <span className="text-sm font-medium">
                              {screenShareControlApi.screenShareLoading
                                ? 'Starting…'
                                : screenShareControlApi.screenShareEnabled
                                  ? 'Stop sharing'
                                  : 'Share screen'}
                            </span>
                          </button>
                          {screenShareControlApi.hasScreenSharePermission ? (
                            <button
                              type="button"
                              className={`flex w-full items-center justify-center gap-2 rounded-md p-2.5 text-base text-white transition-all duration-200 ${
                                screenShareControlApi.isPresentationMode
                                  ? 'border border-purple-500 bg-purple-500 hover:bg-purple-600'
                                  : 'border border-[#444] bg-[#2a2a2a] hover:border-[#555] hover:bg-[#3a3a3a]'
                              }`}
                              onClick={() => screenShareControlApi.togglePresentationMode()}
                              title={
                                screenShareControlApi.isPresentationMode
                                  ? 'Exit presentation mode'
                                  : 'Presentation mode (reduces graphics for smoother screen share)'
                              }
                              data-tour-id="tour-presentation-mode"
                            >
                              {screenShareControlApi.isPresentationMode ? <FaExpand /> : <FaCompress />}
                              <span className="text-sm font-medium">
                                {screenShareControlApi.isPresentationMode
                                  ? 'Exit presentation mode'
                                  : 'Presentation mode'}
                              </span>
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  )}
                </UnityGameDockApp>
              </div>
            )}
          </div>
          {!isPresenterMode && currentRoomId !== 'intersect-main' && (
            <BreakoutClapOverlay bursts={clapBursts} />
          )}
          </>
        )}

          {/* IVS Realtime Panel - Overlay on top of Unity game/stage */}
          {/* Only render when enabled - unmounting completely disconnects and cleans up streams */}
          {isUIVisible && isIVSPanelEnabled && ((stageArnFromRoom && participantTokenFromRoom) || isRoomTransitioning) ? (
            <div 
              className="unity-custom-cursor"
              style={{ 
              visibility: isUnityWorldReady ? 'visible' : 'hidden',
              // Always use fixed positioning for consistent layout regardless of presentation mode
              position: 'fixed' as const,
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              pointerEvents: 'none',
              zIndex: 1000
            }}>
              <IVSRealtimePanel
                key={`panel-${currentRoomId}`}
                stageArn={stageArnFromRoom}
                participantToken={participantTokenFromRoom}
                accessToken={accessToken}
                userId={userIdFromRoom || ivsUserId}
                isIntersectStaff={isIntersectStaffFromRoom}
                externalCollapseControl={true}
                isCollapsedExternal={isIVSPanelCollapsed}
                onCollapseChange={setIsIVSPanelCollapsed}
                onStageViewToggle={handleStageViewToggle}
                onDisablePanel={() => {
                  logger.log('[IVS Panel] Disabling panel - component will unmount');
                  setIsIVSPanelEnabled(false);
                }}
                onScreenShareChange={handleScreenShareChange}
                hasScreenSharePermission={hasScreenSharePermission}
                isPresentationMode={isLowResMode}
                onTogglePresentationMode={handleTogglePresentationMode}
                roomName={currentRoomName}
                isLoading={isRoomTransitioning}
                stagePlaybackUrl={stagePlaybackUrl}
                isStageViewActive={isStageViewMode}
                isStageMuted={isStageMuted}
                onToggleStageMute={() => setIsStageMuted(!isStageMuted)}
                eventId={eventId}
                roomId={currentRoomId}
                dataTourId="tour-video-panel"
                onExposeLocalVideoControls={setLocalVideoControlApi}
                onExposeScreenShareControls={setScreenShareControlApi}
                onScreenShareTutorialRequest={handleScreenShareTutorialRequest}
                cameraPreviewHostRef={videoCameraPreviewHostRef}
                cameraPreviewHostReady={videoDockPopoverOpen}
                dockCameraUx
                onCameraPreviewDismissInDock={() => setVideoDockPopoverOpen(false)}
                onRequestVideoDock={openVideoDockFromFacePin}
              />
            </div>
          ) : null}
          
          {/* IVS Realtime Chat - Overlay on bottom left */}
          {!isLoading && isUIVisible && accessToken && chatRoomArn && (
            <div 
              className="unity-custom-cursor"
              style={{ 
              visibility: isUnityWorldReady ? 'visible' : 'hidden',
              // Always use fixed positioning for consistent layout regardless of presentation mode
              position: 'fixed' as const,
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              pointerEvents: 'none',
              zIndex: 1000
            }}>
              <IVSRealtimeChat
                key={`chat-${currentRoomId}`}
                ref={chatRef}
                roomArn={chatRoomArn}
                messagingEndpoint="wss://edge.ivschat.us-west-2.amazonaws.com"
                accessToken={accessToken}
                userId={userIdFromRoom || ivsUserId}
                roomName={currentRoomName}
                roomId={currentRoomId}
                eventId={eventId}
                podiumPosition={podiumPosition}
                onPodiumTeleport={() => setIsAtPodium(true)}
                onScreenSharePermissionGranted={() => {
                  logger.log('[Unity] Screen share permission granted!');
                  setHasScreenSharePermission(true);
                }}
                onScreenSharePermissionRevoked={() => {
                  logger.log('[Unity] Screen share permission revoked!');
                  screenShareAutoGrantedForRoom.current = null; // Clear auto-grant tracking
                  setHasScreenSharePermission(false);
                }}
                onHandRaiseSignal={handleHandRaiseSignal}
                onClapSignal={handleClapSignal}
                handRaiseDisplayName={handRaiseDisplayName}
                dataTourId="tour-chat-panel"
              />
            </div>
          )}

        {/* NPC Dialog */}
        {showNPCDialog && !isLoading && (
          <NPCDialog
            npcId={currentNPCId}
            currentMessage={(npcDialogues[currentNPCId] || [])[currentNPCMessageIndex] || ''}
            messageIndex={currentNPCMessageIndex}
            totalMessages={(npcDialogues[currentNPCId] || []).length}
            onNext={handleNPCNext}
            onClose={handleNPCClose}
          />
        )}

        {playerClickedPanel && accessToken ? (
          <PlayerClickedPeopleInfoPopover
            accessToken={accessToken}
            userId={playerClickedPanel.userId}
            fallbackDisplayName={playerClickedPanel.displayName}
            onClose={() => setPlayerClickedPanel(null)}
            viewerUserId={userIdFromRoom || ivsUserId || undefined}
            moderatorContext={
              isIntersectStaff && accessToken && chatRoomArn
                ? {
                    chatRef,
                    chatRoomAvailable: Boolean(chatRoomArn),
                    podiumPosition,
                    targetHasRaisedHand: Boolean(
                      handRaiseByUserId[playerClickedPanel.userId.trim()] ||
                        handRaiseByUserId[playerClickedPanel.userId]
                    ),
                    currentViewerUserId: userIdFromRoom || ivsUserId || undefined,
                    onModeratorUnraiseHand: handleModeratorUnraiseHand,
                  }
                : undefined
            }
          />
        ) : null}

        {showBreakoutGuidelines && !isLoading && (
          <BreakoutGuidelinesModal
            open={showBreakoutGuidelines}
            onDismiss={() => setShowBreakoutGuidelines(false)}
          />
        )}

        {/* Area Trigger Interaction Prompt */}
        {showAreaTriggerPrompt && !isLoading && pendingAreaTriggerRoom && (
          <div 
            className="unity-modal-overlay fixed inset-0 flex items-center justify-center pointer-events-none"
            style={{ zIndex: 9999 }}
          >
            <div 
              className="bg-black bg-opacity-80 border-2 border-white rounded-xl px-8 py-4 flex items-center gap-4 shadow-2xl animate-pulse"
              style={{
                backdropFilter: 'blur(8px)',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }}
            >
              <div 
                className="w-12 h-12 bg-white text-black rounded-lg flex items-center justify-center font-bold text-2xl shadow-lg"
                style={{
                  boxShadow: '0 0 20px rgba(255, 255, 255, 0.5)'
                }}
              >
                E
              </div>
              <span className="text-white text-xl font-semibold tracking-wide">
                to enter room
              </span>
            </div>
          </div>
        )}

        {/* Avatar Selection Modal - Highest Priority (after world/server ready, same as dock UI) */}
        {showAvatarSelection && !isLoading && isUnityWorldReady && (
          <div 
            className="unity-modal-overlay absolute inset-0 bg-black bg-opacity-90 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 10002 }}
          >
            <div 
              className="relative bg-gray-900 border-2 border-blue-500 rounded-lg p-8 max-w-2xl mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-3xl font-bold text-white mb-4 text-center">
                Select Your Avatar
              </h2>
              <p className="text-gray-300 mb-8 text-center">
                {isCustomizingAvatar ? 'Customizing your avatar...' : 'Choose an avatar to get started'}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                {avatarOptions.map((avatar) => (
              <button
                    key={avatar.id}
                    onClick={() => customizeActiveAvatar(avatar.id, avatar.characterName)}
                    disabled={isCustomizingAvatar}
                    className={`group relative flex flex-col items-center p-4 bg-gray-800 border-2 border-gray-700 rounded-lg transition-all duration-200 ${
                      isCustomizingAvatar 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:border-blue-500 hover:bg-gray-700 transform hover:scale-105 cursor-pointer'
                    }`}
                  >
                    <img
                      src={avatar.image}
                      alt={`Avatar ${avatar.id}`}
                      className="w-24 h-24 object-contain mb-3"
                    />
                    {isCustomizingAvatar && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Controls Popup Modal */}
        {showControlsPopup && !isLoading && !showAvatarSelection && (
          <div 
            className="unity-modal-overlay absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={handlePopupClose}
            style={{ zIndex: 10001 }}
          >
            <div className="relative max-w-4xl max-h-[90%] p-4">
              <img
                src={tutorialPanel}
                alt="Controls"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on image
              />
              <button
                onClick={handlePopupClose}
                className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors flex items-center justify-center text-lg font-bold"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Respawn Confirmation Popup */}
        {showRespawnPopup && !isLoading && (
          <div 
            className="unity-modal-overlay absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 10001 }}
          >
            <div 
              className="relative bg-gray-900 border-2 border-orange-500 rounded-lg p-8 max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-white mb-4 text-center">
                Respawn to Spawn Point?
              </h2>
              <p className="text-gray-300 mb-6 text-center">
                Would you like to teleport back to the spawn location?
              </p>
              <div className="flex gap-4">
                <button
                  onClick={handleRespawn}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 font-semibold"
                >
                  Yes, Respawn
                </button>
                <button
                  onClick={handleRespawnCancel}
                  className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 font-semibold"
                >
                  No, Thanks
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Respawn Banner - Persistent reminder */}
        {showRespawnBanner && !isLoading && !showRespawnPopup && (
          <div 
            className="unity-custom-cursor fixed top-20 left-1/2 transform -translate-x-1/2 bg-orange-600 bg-opacity-90 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-3"
            style={{ zIndex: 10001 }}
          >
            <span className="text-sm font-semibold">
              Press "R" to teleport back to spawn
            </span>
            <button
              onClick={() => setShowRespawnBanner(false)}
              className="ml-2 text-white hover:text-gray-200 transition-colors"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Onboarding Tour */}
        {showOnboarding && !isLoading && !showAvatarSelection && (
          <OnboardingTour
            isVisible={showOnboarding}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingSkip}
          />
        )}

        {isLoading && (
          <div className="absolute inset-0 bg-black z-20" style={{ zIndex: 10002 }}>
            <img
              src={loadingImages[currentImageIndex]}
              alt="Loading"
              className="w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
              style={{ opacity: imageOpacity }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            </div>
          </div>
        )}
        {isLoading && (
          <div
            id="unity-loading-bar"
            className="absolute bottom-16 left-4 right-4 h-2 bg-gray-200 rounded-full overflow-hidden z-20"
            style={{ zIndex: 10003 }}
          >
            <div
              id="unity-progress-bar-full"
              className="h-full bg-blue-500 transition-all duration-300 ease-out"
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>
        )}
       </div>
      </div>
  );
};

// Wrapper component that provides FocusProvider
const UnityGame: React.FC<UnityGameProps> = (props) => {
  return (
    <FocusProvider>
      <UnityGameInner {...props} />
    </FocusProvider>
  );
};

export default UnityGame;
